import {
  ArrowLeftRight,
  BarChart3,
  BookCopy,
  Clapperboard,
  CreditCard,
  Headphones,
  Languages,
  LayoutDashboard,
  Mail,
  Megaphone,
  MessageSquareText,
  PackageSearch,
  Printer,
  RefreshCcw,
  Settings,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import InsightCard from '../components/InsightCard';
import OverviewDashboard, { type OverviewSideListItem, type OverviewTopPageRow } from '../components/OverviewDashboard';
import { OrderStatusTracker } from '../components/OrderStatusTracker';
import PodManagerDashboardPanel from '../components/PodManagerDashboardPanel';
import SparklineCard from '../components/SparklineCard';
import TransferProcessBoard from '../components/TransferProcessBoard';
import {
  booksService,
  catalogService,
  notificationService,
  paymentService,
  podBooksService,
  userService,
  type NotificationRecord,
} from '../../../shared/services';
import { authService } from '../../../shared/services/authService';
import { orderService } from '../../../shared/services/orderService';
import { API_BASE_URL, BACKEND_BASE_URL } from '../../../shared/config/api';
import AdminRefundPage from './AdminRefundPage';
import AdminContactMessagesPage from './AdminContactMessagesPage';
import { useShop } from '../../../shared/store';
import type {
  PaymentHistoryEntry,
  PaymentHistoryPagination,
  PaymentHistoryStats,
} from '../../../shared/services/paymentService';
import type { OrderStatus, PaymentMethod, Product, UserRole } from '../../../shared/types';

type AdminSection =
  | 'overview'
  | 'pod'
  | 'books'
  | 'animations'
  | 'audio'
  | 'users'
  | 'orders'
  | 'refunds'
  | 'transfers'
  | 'payment-history'
  | 'announcements'
  | 'translation'
  | 'analytics'
  | 'reviews'
  | 'contact'
  | 'security'
  | 'settings';
type AdminAudience = 'kids' | 'adult' | 'adults' | 'mixed' | 'public';
type FeedbackStatus = 'idle' | 'pending' | 'success' | 'error';
type ContentFilterScope = 'all' | 'user';

interface UploadedByOption {
  value: string;
  label: string;
  meta: string;
  count: number;
}

interface FormFeedback {
  status: FeedbackStatus;
  message: string;
}

interface LegacyBookRow {
  id: string;
  title: string;
  buyerName: string;
  buyerEmail: string;
  amount: number;
  currency: string;
  status: string;
  transactionRef?: string;
  paymentType?: string;
  createdAt: string;
}

const adminOrderStatusOptions: OrderStatus[] = ['Processed', 'Shipped', 'En Route', 'Arrived', 'Cancelled'];
const isAutomaticDigitalOrder = (order: { fulfillmentType?: 'digital' | 'delivery'; status: OrderStatus }): boolean =>
  order.fulfillmentType === 'digital' || order.status === 'Pending' || order.status === 'Paid' || order.status === 'Failed';

const toAdminOrderStatus = (status: OrderStatus): OrderStatus => {
  if (status === 'Failed') {
    return 'Cancelled';
  }

  return status;
};

const toBackendWorkflowStatus = (status: OrderStatus): string => {
  if (status === 'Processed') return 'processing';
  if (status === 'En Route') return 'en_route';
  if (status === 'Arrived') return 'delivered';
  return status.toLowerCase();
};

const normalizeLegacyBookRows = (payload: unknown): LegacyBookRow[] => {
  const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

  const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim()
      ? value.trim()
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : undefined;

  const asBoolean = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (value === 1) return true;
      if (value === 0) return false;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
      if (['false', 'no', 'n', '0'].includes(normalized)) return false;
    }
    return undefined;
  };

  const asNumber = (value: unknown, fallbackValue = 0): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
    return fallbackValue;
  };

  const record = asRecord(payload);
  const dataRecord = asRecord(record.data);
  const candidates: unknown[] = [
    ...(Array.isArray(record.data) ? record.data : []),
    ...(Array.isArray(record.items) ? record.items : []),
    ...(Array.isArray(dataRecord.items) ? dataRecord.items : []),
    ...(Array.isArray(dataRecord.data) ? dataRecord.data : []),
    ...(Array.isArray(payload) ? (payload as unknown[]) : []),
  ];

  const rows: LegacyBookRow[] = [];

  candidates.forEach((entryValue) => {
    const entry = asRecord(entryValue);
    const paidBookInfo = asRecord(entry.paidBookInfo);
    const buyerInfo = asRecord(entry.buyerInfo);
    const sellerInfo = asRecord(entry.sellerInfo);

    const embeddedBook = asRecord(entry.book ?? entry.product ?? entry.item ?? entry.content ?? paidBookInfo ?? entryValue);
    const embeddedUser = asRecord(entry.user ?? entry.buyer ?? entry.customer ?? buyerInfo ?? sellerInfo);

    const buyerNameFromInfo = [asString(buyerInfo.firstname), asString(buyerInfo.lastname)]
      .filter(Boolean)
      .join(' ')
      .trim();

    const id =
      asString(entry.id) ||
      asString(entry._id) ||
      asString(entry.paymentId) ||
      asString(entry.payment_id) ||
      asString(entry.bookId) ||
      asString(entry.book_id) ||
      asString(embeddedBook.id) ||
      asString(embeddedBook._id) ||
      asString(entry.productId) ||
      asString(entry.contentId);

    const title =
      asString(entry.title) ||
      asString(entry.name) ||
      asString(embeddedBook.title) ||
      asString(embeddedBook.name) ||
      asString(paidBookInfo.title) ||
      asString(paidBookInfo.name) ||
      asString(embeddedBook.productName) ||
      'Untitled';

    if (!id) {
      return;
    }

    const buyerName =
      asString(entry.buyerName) ||
      asString(entry.fullname) ||
      asString(embeddedUser.fullname) ||
      asString(embeddedUser.name) ||
      buyerNameFromInfo ||
      asString(buyerInfo.username) ||
      'Buyer';
    const buyerEmail =
      asString(entry.buyerEmail) ||
      asString(entry.email) ||
      asString(embeddedUser.email) ||
      asString(buyerInfo.email) ||
      'unknown@haapa.store';

    const amount = asNumber(entry.amount, asNumber(entry.price, asNumber(embeddedBook.price, asNumber(paidBookInfo.price, 0))));
    const currency = asString(entry.currency) || asString(embeddedBook.currency) || asString(paidBookInfo.currency) || 'RWF';
    const rawStatus = asString(entry.status) || asString(entry.paymentStatus) || asString(entry.state) || 'unknown';
    const sellerPaid = asBoolean(entry.seller_paid) ?? asBoolean(entry.sellerPaid) ?? false;
    const sellerReceived =
      asBoolean(entry.seller_received_payment) ??
      asBoolean(entry.sellerReceivedPayment) ??
      asBoolean(entry.seller_received) ??
      false;
    const paymentVerified =
      asBoolean(entry.verified) ??
      asBoolean(entry.paymentVerified) ??
      asBoolean(entry.payment_verified) ??
      asBoolean(entry.verification) ??
      false;

    const status = sellerPaid || sellerReceived || paymentVerified ? 'paid' : rawStatus;
    const paymentType = asString(entry.payment_type) || asString(entry.paymentType) || asString(entry.payment_provider);
    const transactionRef =
      asString(entry.tx_ref) ||
      asString(entry.flw_ref) ||
      asString(entry.reference) ||
      asString(entry.transactionRef) ||
      asString(entry.transaction_id) ||
      asString(entry.transactionId) ||
      undefined;
    const createdAt = asString(entry.createdAt) || asString(entry.paidAt) || new Date().toISOString();

    rows.push({
      id,
      title,
      buyerName,
      buyerEmail,
      amount,
      currency,
      status,
      ...(transactionRef ? { transactionRef } : {}),
      ...(paymentType ? { paymentType } : {}),
      createdAt,
    });
  });

  const unique = new Map<string, LegacyBookRow>();
  rows.forEach((row) => {
    const key = `${row.id}-${row.title}-${row.buyerEmail}`;
    if (!unique.has(key)) {
      unique.set(key, row);
    }
  });

  return [...unique.values()].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
};

const sections: AdminSection[] = [
  'overview',
  'pod',
  'books',
  'animations',
  'audio',
  'users',
  'orders',
  'refunds',
  'transfers',
  'payment-history',
  'announcements',
  'translation',
  'analytics',
  'reviews',
  'contact',
  'security',
  'settings',
];

const adminNavItemsConfig: Array<Omit<DashboardNavItem, 'label'> & { labelKey?: string; label?: string }> = [
  { id: 'overview', labelKey: 'dashboardAdmin.nav.overview', to: '/admin/dashboard/overview', icon: LayoutDashboard },
  { id: 'pod', label: 'POD', to: '/admin/dashboard/pod', icon: Printer },
  { id: 'books', labelKey: 'dashboardAdmin.nav.books', to: '/admin/dashboard/books', icon: BookCopy },
  { id: 'animations', labelKey: 'common.animations', to: '/admin/dashboard/animations', icon: Clapperboard },
  { id: 'audio', labelKey: 'common.audio', to: '/admin/dashboard/audio', icon: Headphones },
  { id: 'users', labelKey: 'dashboardAdmin.nav.users', to: '/admin/dashboard/users', icon: UsersRound },
  { id: 'orders', labelKey: 'dashboardAdmin.nav.orders', to: '/admin/dashboard/orders', icon: PackageSearch },
  { id: 'refunds', label: 'Refunds', to: '/admin/dashboard/refunds', icon: RefreshCcw },
  { id: 'transfers', label: 'Transfers', to: '/admin/dashboard/transfers', icon: ArrowLeftRight },
  { id: 'payment-history', label: 'Payment History', to: '/admin/dashboard/payment-history', icon: CreditCard },
  { id: 'announcements', labelKey: 'dashboardAdmin.nav.announcements', to: '/admin/dashboard/announcements', icon: Megaphone },
  { id: 'translation', labelKey: 'dashboardAdmin.nav.translation', to: '/admin/dashboard/translation', icon: Languages },
  { id: 'analytics', labelKey: 'dashboardAdmin.nav.analytics', to: '/admin/dashboard/analytics', icon: BarChart3 },
  { id: 'reviews', labelKey: 'dashboardAdmin.nav.reviews', to: '/admin/dashboard/reviews', icon: MessageSquareText },
  { id: 'contact', label: 'Contact Messages', to: '/admin/dashboard/contact', icon: Mail },
  { id: 'security', labelKey: 'dashboardAdmin.nav.security', to: '/admin/dashboard/security', icon: ShieldCheck },
  { id: 'settings', labelKey: 'dashboardAdmin.nav.settings', to: '/admin/dashboard/settings', icon: Settings },
];

const sectionMetaKeys: Record<
  AdminSection,
  { titleKey?: string; descriptionKey?: string; fallbackTitle?: string; fallbackDescription?: string }
> = {
  overview: { titleKey: 'dashboardAdmin.sections.overview.title', descriptionKey: 'dashboardAdmin.sections.overview.description' },
  pod: {
    fallbackTitle: 'POD Dashboard',
    fallbackDescription: 'Monitor Print-on-Demand projects, previews, and requests.',
  },
  books: { titleKey: 'dashboardAdmin.sections.books.title', descriptionKey: 'dashboardAdmin.sections.books.description' },
  animations: {
    fallbackTitle: 'Animation Management',
    fallbackDescription: 'Manage animation titles, quality review, and publication status.',
  },
  audio: {
    fallbackTitle: 'Audio Management',
    fallbackDescription: 'Manage audio titles, runtime quality checks, and publication status.',
  },
  users: { titleKey: 'dashboardAdmin.sections.users.title', descriptionKey: 'dashboardAdmin.sections.users.description' },
  orders: { titleKey: 'dashboardAdmin.sections.orders.title', descriptionKey: 'dashboardAdmin.sections.orders.description' },
  refunds: {
    fallbackTitle: 'Refund Management',
    fallbackDescription: 'Review and manage customer refund requests.',
  },
  transfers: {
    fallbackTitle: 'Transfer Process',
    fallbackDescription: 'Monitor incoming and outgoing money transfers with live status tracking.',
  },
      'payment-history': {
    fallbackTitle: 'Payment History',
    fallbackDescription: 'See book and media payments with buyer details, payment method, and paid date-time.',
  },
  announcements: {
    titleKey: 'dashboardAdmin.sections.announcements.title',
    descriptionKey: 'dashboardAdmin.sections.announcements.description',
  },
  translation: { titleKey: 'dashboardAdmin.sections.translation.title', descriptionKey: 'dashboardAdmin.sections.translation.description' },
  analytics: { titleKey: 'dashboardAdmin.sections.analytics.title', descriptionKey: 'dashboardAdmin.sections.analytics.description' },
  reviews: { titleKey: 'dashboardAdmin.sections.reviews.title', descriptionKey: 'dashboardAdmin.sections.reviews.description' },
  contact: { fallbackTitle: 'Contact Messages', fallbackDescription: 'View all messages submitted from the Contact page.' },
  security: { titleKey: 'dashboardAdmin.sections.security.title', descriptionKey: 'dashboardAdmin.sections.security.description' },
  settings: { titleKey: 'dashboardAdmin.sections.settings.title', descriptionKey: 'dashboardAdmin.sections.settings.description' },
};

const initialFeedback: FormFeedback = { status: 'idle', message: '' };

const formatUserRoleLabel = (role: UserRole): string => {
  if (role === 'admin') {
    return 'Admin';
  }
  if (role === 'manager') {
    return 'Manager';
  }
  if (role === 'vendor') {
    return 'Vendor';
  }
  return 'Buyer';
};

const getUserRoleTone = (role: UserRole): string => {
  if (role === 'admin') {
    return 'bg-red-100 text-red-700';
  }
  if (role === 'manager') {
    return 'bg-sky-100 text-sky-700';
  }
  if (role === 'vendor') {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-emerald-100 text-emerald-700';
};

const buildInitials = (name: string): string => {
  const tokens = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (tokens.length === 0) {
    return 'U';
  }

  return tokens.map((token) => token[0]?.toUpperCase() ?? '').join('');
};

const detectContentFormat = (
  product: Pick<
    Product,
    'categoryKey' | 'nameKey' | 'descriptionKey' | 'imageUrl' | 'mediaType' | 'audioUrl' | 'videoUrl' | 'hasAudioUrl' | 'hasVideoUrl'
  >,
): 'audio' | 'animation' | 'book' => {
  if (product.mediaType === 'audio' || product.hasAudioUrl || Boolean(product.audioUrl)) {
    return 'audio';
  }

  if (product.mediaType === 'animation' || product.hasVideoUrl || Boolean(product.videoUrl)) {
    return 'animation';
  }

  const searchable = `${product.categoryKey} ${product.nameKey} ${product.descriptionKey} ${product.imageUrl}`.toLowerCase();
  if (searchable.includes('audio') || searchable.includes('voice') || searchable.includes('podcast')) {
    return 'audio';
  }
  if (searchable.includes('animation') || searchable.includes('anim') || searchable.includes('cartoon')) {
    return 'animation';
  }
  return 'book';
};

const normalizeComparableValue = (value?: string): string =>
  value?.trim().toLowerCase() ?? '';

const getProductUploaderDisplayName = (product: Product): string =>
  product.uploaderName?.trim() ||
  product.authorName?.trim() ||
  product.author?.trim() ||
  'Unknown uploader';

const getProductUploaderDisplayMeta = (product: Product): string =>
  product.uploaderEmail?.trim() ||
  product.publishedCompany?.trim() ||
  'Uploader details not available';

const buildUploadedByOptions = (items: Product[]): UploadedByOption[] => {
  const grouped = new Map<string, UploadedByOption>();

  items.forEach((product) => {
    const label = getProductUploaderDisplayName(product);
    const value = normalizeComparableValue(label);
    if (!value || value === 'unknown uploader') {
      return;
    }

    const existing = grouped.get(value);
    if (existing) {
      grouped.set(value, { ...existing, count: existing.count + 1 });
      return;
    }

    grouped.set(value, {
      value,
      label,
      meta: getProductUploaderDisplayMeta(product),
      count: 1,
    });
  });

  return [...grouped.values()].sort((first, second) => first.label.localeCompare(second.label));
};

const buildSlug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getFeedbackTone = (status: FeedbackStatus): string => {
  if (status === 'success') {
    return 'text-emerald-700';
  }
  if (status === 'error') {
    return 'text-red-600';
  }
  if (status === 'pending') {
    return 'text-orange-600';
  }

  return 'text-slate-500';
};

const getStatusTone = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'paid' ||
    normalized === 'success' ||
    normalized === 'successful' ||
    normalized === 'completed' ||
    normalized === 'delivered' ||
    normalized === 'arrived'
  ) {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (normalized === 'not paid' || normalized === 'not_paid' || normalized === 'unpaid') {
    return 'bg-rose-100 text-rose-700';
  }
  if (normalized === 'failed' || normalized === 'cancelled' || normalized === 'canceled') {
    return 'bg-red-100 text-red-700';
  }
  if (normalized === 'processed' || normalized === 'pending' || normalized === 'processing') {
    return 'bg-amber-100 text-amber-700';
  }
  if (normalized === 'shipped') {
    return 'bg-sky-100 text-sky-700';
  }
  if (normalized === 'en route' || normalized === 'en_route') {
    return 'bg-indigo-100 text-indigo-700';
  }
  return 'bg-slate-100 text-slate-700';
};

const getPaymentStatusLabel = (value?: string): 'Paid' | 'Not Paid' => {
  const normalized = value?.trim().toLowerCase() ?? '';
  if (
    normalized === 'paid' ||
    normalized === 'successful' ||
    normalized === 'success' ||
    normalized === 'completed' ||
    normalized === 'succeeded'
  ) {
    return 'Paid';
  }

  return 'Not Paid';
};

const isSuccessfulPaymentStatus = (value?: string): boolean => getPaymentStatusLabel(value) === 'Paid';

const paymentMethodLabels: Record<PaymentMethod, string> = {
  card: 'Card',
  cod: 'Cash on Delivery',
  paypal: 'PayPal',
  mobileMoney: 'Mobile Money',
};

const formatDateTime = (value: string): string => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(parsedDate);
};

const formatMoney = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-RW', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
};

const toActionErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error instanceof Error && error.message.trim()) {
    if (error.message.toLowerCase().includes('unauthorized')) {
      return 'Unauthorized. Please sign in again to continue.';
    }
    return error.message;
  }

  return fallbackMessage;
};

import ErrorBoundary from '../../../shared/components/ErrorBoundary';

const AdminDashboardPage = () => {
  const { t } = useTranslation();
  const params = useParams<{ section?: string }>();
  const activeSection = sections.includes(params.section as AdminSection)
    ? (params.section as AdminSection)
    : 'overview';
  const {
    currentUser,
    users,
    orders,
    products,
    categories,
    isUsersLoading,
    isCatalogLoading,
    themeMode,
    refreshUsers,
    refreshCatalog,
    refreshOrders,
    deleteProduct,
  } = useShop();
  const [categoryName, setCategoryName] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [categoryAudience, setCategoryAudience] = useState<AdminAudience>('mixed');
  const [categoryFeedback, setCategoryFeedback] = useState<FormFeedback>(initialFeedback);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'buyer' | 'vendor'>('buyer');
  const [newUserPhotoUrl, setNewUserPhotoUrl] = useState('');
  const [userCreationFeedback, setUserCreationFeedback] = useState<FormFeedback>(initialFeedback);
  const [productTitleKey, setProductTitleKey] = useState('');
  const [productDescriptionKey, setProductDescriptionKey] = useState('');
  const [contentCreationMode, setContentCreationMode] = useState<'book' | 'audio' | 'animation' | 'pod'>('book');
  const [productCategoryKey, setProductCategoryKey] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [contentTags, setContentTags] = useState('');
  const [contentAuthor, setContentAuthor] = useState('');
  const [contentCompany, setContentCompany] = useState('');
  const [contentAudioFile, setContentAudioFile] = useState<File | null>(null);
  const [contentVideoFile, setContentVideoFile] = useState<File | null>(null);
  const [contentBookFile, setContentBookFile] = useState<File | null>(null);
  const [contentCoverFile, setContentCoverFile] = useState<File | null>(null);
  const [podPageCount, setPodPageCount] = useState('');
  const [podSize, setPodSize] = useState('');
  const [podColorType, setPodColorType] = useState('');
  const [podCoverType, setPodCoverType] = useState('');
  const [podInteriorPdfFile, setPodInteriorPdfFile] = useState<File | null>(null);
  const [podCoverPdfFile, setPodCoverPdfFile] = useState<File | null>(null);
  const [podInteriorPdfUrl, setPodInteriorPdfUrl] = useState('');
  const [podCoverPdfUrl, setPodCoverPdfUrl] = useState('');
  const [mtnMoMoNumber, setMtnMoMoNumber] = useState('');
  const [airtelMoneyNumber, setAirtelMoneyNumber] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [acceptHaapaTerms, setAcceptHaapaTerms] = useState(false);
  // Legacy fields kept for edit/update flows
  const [productCurrency, setProductCurrency] = useState('$');
  const [productLanguage, setProductLanguage] = useState('en');
  const [productAudience, setProductAudience] = useState<'kids' | 'adult' | 'adults' | 'mixed' | 'public'>('public');
  const [productSlug, setProductSlug] = useState('');
  const [productCoverUrl, setProductCoverUrl] = useState('');
  const [productContentUrl, setProductContentUrl] = useState('');
  const [productPrintPrice, setProductPrintPrice] = useState('');
  const [productPodProjectId, setProductPodProjectId] = useState('');
  const [bookFormat, setBookFormat] = useState('digital');
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [trailerFile, setTrailerFile] = useState<File | null>(null);
  const [fullVideoFile, setFullVideoFile] = useState<File | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [productCoverFile, setProductCoverFile] = useState<File | null>(null);
  const [productKind, setProductKind] = useState<'book' | 'audio' | 'animation'>('book');
  const [contentFilterScope, setContentFilterScope] = useState<ContentFilterScope>('all');
  const [selectedUploadedBy, setSelectedUploadedBy] = useState('');
  const [productFormResetKey, setProductFormResetKey] = useState(0);
  const [productFeedback, setProductFeedback] = useState<FormFeedback>(initialFeedback);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [orderUpdateFeedback, setOrderUpdateFeedback] = useState<FormFeedback>(initialFeedback);
  const [activeOrderUpdateId, setActiveOrderUpdateId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [paymentHistoryRows, setPaymentHistoryRows] = useState<PaymentHistoryEntry[]>([]);
  const [paymentHistoryStats, setPaymentHistoryStats] = useState<PaymentHistoryStats | null>(null);
  const [paymentHistoryPagination, setPaymentHistoryPagination] = useState<PaymentHistoryPagination | null>(null);
  const [paymentHistoryRequestOk, setPaymentHistoryRequestOk] = useState<boolean | null>(null);
  const [isPaymentHistoryLoading, setIsPaymentHistoryLoading] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState('');
  const [legacyPaidBooks, setLegacyPaidBooks] = useState<LegacyBookRow[]>([]);
  const [legacyUnpaidBooks, setLegacyUnpaidBooks] = useState<LegacyBookRow[]>([]);
  const [isLegacyPaidBooksLoading, setIsLegacyPaidBooksLoading] = useState(false);
  const [isLegacyUnpaidBooksLoading, setIsLegacyUnpaidBooksLoading] = useState(false);
  const [legacyPaidBooksError, setLegacyPaidBooksError] = useState('');
  const [legacyUnpaidBooksError, setLegacyUnpaidBooksError] = useState('');
  const [notificationAudience, setNotificationAudience] = useState<'all' | 'creators' | 'preferred'>('all');
  const [preferredNotificationEmail, setPreferredNotificationEmail] = useState('');
  const [notificationBodyDraft, setNotificationBodyDraft] = useState('');
  const [adminNotifications, setAdminNotifications] = useState<NotificationRecord[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationFeedback, setNotificationFeedback] = useState<FormFeedback>(initialFeedback);
  const resolveTranslation = (key: string | undefined, fallback: string): string => {
    if (!key) {
      return fallback;
    }
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const handleContentCreationModeChange = (nextMode: 'book' | 'audio' | 'animation' | 'pod') => {
    setContentCreationMode(nextMode);
    setProductFeedback(initialFeedback);
    setUploadProgress(null);
    setContentAudioFile(null);
    setContentVideoFile(null);
    setContentBookFile(null);
    setContentCoverFile(null);
    setPodInteriorPdfFile(null);
    setPodCoverPdfFile(null);
    setProductFormResetKey((value) => value + 1);
  };

  const adminNavItems = useMemo<DashboardNavItem[]>(
    () =>
      adminNavItemsConfig.map((item) => ({
        ...item,
        label: item.label ?? resolveTranslation(item.labelKey, item.id),
      })),
    [t],
  );
  const sectionMeta = useMemo(
    () =>
      Object.fromEntries(
      Object.entries(sectionMetaKeys).map(([sectionKey, meta]) => [
        sectionKey,
        {
          title: resolveTranslation(meta.titleKey, meta.fallbackTitle ?? sectionKey),
          description: resolveTranslation(meta.descriptionKey, meta.fallbackDescription ?? ''),
          },
        ]),
      ) as Record<AdminSection, { title: string; description: string }>,
    [t],
  );

  const loadAdminNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const data = await notificationService.getAllAdmin();
      setAdminNotifications(
        [...data].sort((left, right) => {
          const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
          const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
          return rightTime - leftTime;
        }),
      );
    } catch (error: unknown) {
      setNotificationFeedback({ status: 'error', message: toActionErrorMessage(error, 'Unable to load notifications.') });
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection !== 'announcements') {
      return;
    }
    void loadAdminNotifications();
  }, [activeSection, loadAdminNotifications]);

  const publishNotifications = useCallback(async () => {
    setNotificationFeedback({ status: 'pending', message: '' });
    const body = notificationBodyDraft.trim();
    if (!body) {
      setNotificationFeedback({ status: 'error', message: 'Notification message is required.' });
      return;
    }

    const userEmails = users
      .map((user) => user.email?.trim())
      .filter((email): email is string => Boolean(email));

    const dedupe = (values: string[]) => [...new Set(values.map((v) => v.trim()).filter(Boolean))];

    let recipients: string[] = [];
    if (notificationAudience === 'preferred') {
      const email = preferredNotificationEmail.trim();
      if (!email) {
        setNotificationFeedback({ status: 'error', message: 'Select a user email.' });
        return;
      }
      recipients = [email];
    } else if (notificationAudience === 'creators') {
      recipients = dedupe(
        users
          .filter((user) => user.role === 'vendor')
          .map((user) => user.email?.trim() || '')
          .filter(Boolean),
      );
    } else {
      recipients = dedupe(userEmails);
    }

    if (recipients.length === 0) {
      setNotificationFeedback({ status: 'error', message: 'No recipients found.' });
      return;
    }

    try {
      for (const email of recipients) {
        await notificationService.create({ sendTo: email, notificationBody: body });
      }
      setNotificationBodyDraft('');
      setPreferredNotificationEmail('');
      setNotificationAudience('all');
      setNotificationFeedback({ status: 'success', message: `Sent to ${recipients.length} user(s).` });
      await loadAdminNotifications();
    } catch (error: unknown) {
      setNotificationFeedback({ status: 'error', message: toActionErrorMessage(error, 'Unable to send notification.') });
    }
  }, [loadAdminNotifications, notificationAudience, notificationBodyDraft, preferredNotificationEmail, users]);

  const buildPaymentHistoryFallbackRows = useCallback((): PaymentHistoryEntry[] => {
    return orders
      .filter((order) => isSuccessfulPaymentStatus(order.paymentStatus))
      .map((order) => ({
        id: order.paymentIntentId || order.paymentReference || order.id,
        transactionRef: order.paymentReference || order.paymentIntentId || order.id,
        amount: order.total,
        currency: order.currency || '$',
        status: order.paymentStatus || 'paid',
        orderStatus: order.status,
        platformCommission: Math.round(order.total * 0.3),
        creatorEarnings: Math.max(0, order.total - Math.round(order.total * 0.3)),
        customer: {
          email: order.buyerEmail,
          name: order.buyerName,
        },
        createdAt: order.createdAt,
        orderId: order.id,
        user: {
          id: order.buyerId || '',
          fullname: order.buyerName,
          email: order.buyerEmail,
        },
      }))
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
  }, [orders]);

  const mergePaymentHistoryRows = useCallback(
    (rows: PaymentHistoryEntry[]): PaymentHistoryEntry[] => {
      const merged = new Map<string, PaymentHistoryEntry>();

      [...rows, ...buildPaymentHistoryFallbackRows()].forEach((row) => {
        const key = `${row.orderId}-${row.transactionRef || row.id}`;
        if (!merged.has(key)) {
          merged.set(key, row);
        }
      });

      return [...merged.values()].sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
      );
    },
    [buildPaymentHistoryFallbackRows],
  );

  const loadPaymentHistory = useCallback(async () => {
    setIsPaymentHistoryLoading(true);
    setPaymentHistoryError('');

    try {
      const history = await paymentService.getAdminHistoryAll();
      const mergedRows = mergePaymentHistoryRows(history.data);
      setPaymentHistoryRows(mergedRows);
      setPaymentHistoryStats(
        history.stats ?? {
          total: mergedRows.length,
          successful: mergedRows.filter((row) => isSuccessfulPaymentStatus(row.status)).length,
          pending: mergedRows.filter((row) => row.status.trim().toLowerCase() === 'pending').length,
          failed: mergedRows.filter((row) => ['failed', 'cancelled', 'canceled'].includes(row.status.trim().toLowerCase())).length,
          totalAmount: mergedRows.reduce((sum, row) => sum + row.amount, 0),
        },
      );
      setPaymentHistoryPagination(history.pagination ?? null);
      setPaymentHistoryRequestOk(history.success);
    } catch (error: unknown) {
      const fallbackRows = buildPaymentHistoryFallbackRows();
      setPaymentHistoryRows(fallbackRows);
      setPaymentHistoryStats(
        fallbackRows.length > 0
          ? {
              total: fallbackRows.length,
              successful: fallbackRows.length,
              pending: 0,
              failed: 0,
              totalAmount: fallbackRows.reduce((sum, row) => sum + row.amount, 0),
            }
          : null,
      );
      setPaymentHistoryPagination(null);
      setPaymentHistoryRequestOk(fallbackRows.length > 0 ? true : false);
      setPaymentHistoryError(
        fallbackRows.length > 0
          ? ''
          : toActionErrorMessage(error, 'Unable to load payment history.'),
      );
    } finally {
      setIsPaymentHistoryLoading(false);
    }
  }, [buildPaymentHistoryFallbackRows, mergePaymentHistoryRows]);

  const loadLegacyPaidBooks = useCallback(async () => {
    setIsLegacyPaidBooksLoading(true);
    setLegacyPaidBooksError('');
    try {
      const payload = await paymentService.getPaidBooksAllAdmin();
      setLegacyPaidBooks(normalizeLegacyBookRows(payload));
    } catch (error: unknown) {
      const message = toActionErrorMessage(error, 'Unable to load paid books.');
      setLegacyPaidBooks([]);
      setLegacyPaidBooksError(message);
    } finally {
      setIsLegacyPaidBooksLoading(false);
    }
  }, []);

  const loadLegacyUnpaidBooks = useCallback(async () => {
    setIsLegacyUnpaidBooksLoading(true);
    setLegacyUnpaidBooksError('');
    try {
      const payload = await paymentService.getUnpaidBooksAdmin();
      setLegacyUnpaidBooks(normalizeLegacyBookRows(payload));
    } catch (error: unknown) {
      const message = toActionErrorMessage(error, 'Unable to load unpaid books.');
      setLegacyUnpaidBooks([]);
      setLegacyUnpaidBooksError(message);
    } finally {
      setIsLegacyUnpaidBooksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection !== 'payment-history') {
      return;
    }

    if (legacyPaidBooks.length === 0 && !isLegacyPaidBooksLoading && !legacyPaidBooksError) {
      void loadLegacyPaidBooks();
    }

    if (legacyUnpaidBooks.length === 0 && !isLegacyUnpaidBooksLoading && !legacyUnpaidBooksError) {
      void loadLegacyUnpaidBooks();
    }
  }, [
    activeSection,
    isLegacyPaidBooksLoading,
    isLegacyUnpaidBooksLoading,
    legacyPaidBooks.length,
    legacyPaidBooksError,
    legacyUnpaidBooks.length,
    legacyUnpaidBooksError,
    loadLegacyPaidBooks,
    loadLegacyUnpaidBooks,
  ]);

  useEffect(() => {
    if (categories.length > 0 && !productCategoryKey) {
      const first = categories[0];
      setProductCategoryKey(first.slug || first.id);
    }
  }, [categories, productCategoryKey]);

  useEffect(() => {
    if (orderUpdateFeedback.status === 'idle') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setOrderUpdateFeedback(initialFeedback);
    }, orderUpdateFeedback.status === 'pending' ? 10000 : 2600);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [orderUpdateFeedback]);

  const stats = useMemo(() => {
    const revenue = orders.reduce((sum, order) => sum + order.total, 0);
    const processing = orders.filter((order) => order.status === 'Processed').length;
    const totalUsers = users.length;

    return {
      revenue,
      processing,
      users: totalUsers,
    };
  }, [orders, users.length]);
  const roleDistribution = useMemo(() => {
    const buyers = users.filter((user) => user.role === 'buyer').length;
    const vendors = users.filter((user) => user.role === 'vendor').length;
    const admins = users.filter((user) => user.role === 'admin' || user.role === 'manager').length;
    return { buyers, vendors, admins };
  }, [users]);
  const hasAuthToken = Boolean(authService.getToken());
  const formatCompactNumber = (value: number): string => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value);
  const formatDecimalCompact = (value: number): string =>
    new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  const adminPerformance = useMemo(() => {
    const revenueByMonth = Array.from({ length: 12 }, () => 0);
    const ordersByMonth = Array.from({ length: 12 }, () => 0);

    orders.forEach((order) => {
      const parsedDate = new Date(order.createdAt);
      if (Number.isNaN(parsedDate.getTime())) {
        return;
      }

      const monthIndex = parsedDate.getMonth();
      revenueByMonth[monthIndex] += order.total;
      ordersByMonth[monthIndex] += 1;
    });

    const normalizedLine = ordersByMonth.map((value) => (value === 0 ? 1 : value));
    return { bars: revenueByMonth, line: normalizedLine };
  }, [orders]);
  const adminTopPages = useMemo<OverviewTopPageRow[]>(
    () => [
      { path: 'admin/overview', pageViews: 365 + orders.length * 7, exitRate: 4.4 },
      { path: 'admin/orders', pageViews: 326 + orders.length * 5, exitRate: 8.2 },
      { path: 'admin/books', pageViews: 254 + products.length * 4, exitRate: 11.7 },
      { path: 'admin/users', pageViews: 203 + stats.users, exitRate: 6.2 },
      { path: 'admin/reviews', pageViews: 158 + stats.processing * 9, exitRate: 13.1 },
    ],
    [orders.length, products.length, stats.processing, stats.users],
  );
  const deliveredOrders = useMemo(() => orders.filter((order) => order.status === 'Arrived').length, [orders]);
  const conversionRate = useMemo(() => {
    if (orders.length === 0) {
      return 65.2;
    }

    return (deliveredOrders / orders.length) * 100;
  }, [deliveredOrders, orders.length]);

  const productReviews = useMemo(
    () =>
      products.flatMap((product) =>
        product.reviews.map((review) => ({
          productId: product.id,
          productName: t(product.nameKey),
          rating: review.rating,
          author: review.author,
          commentKey: review.commentKey,
        })),
      ),
    [products, t],
  );
  const bookProducts = useMemo(
    () => products.filter((product) => detectContentFormat(product) === 'book'),
    [products],
  );
  const animationProducts = useMemo(
    () => products.filter((product) => detectContentFormat(product) === 'animation'),
    [products],
  );
  const bookNominees = useMemo<OverviewSideListItem[]>(() => {
    const sorted = [...products].sort((first, second) => {
      const ratingDelta = (second.rating ?? 0) - (first.rating ?? 0);
      if (ratingDelta !== 0) return ratingDelta;
      return (second.reviewCount ?? 0) - (first.reviewCount ?? 0);
    });

    return sorted.slice(0, 10).map((product) => ({
      id: product.id,
      title: t(product.nameKey),
      subtitle: product.authorName || product.author || product.categoryKey,
      imageUrl: product.coverUrl || product.imageUrl,
      href: '/admin/dashboard/books',
      rating: product.rating,
      reviewCount: product.reviewCount,
    }));
  }, [products, t]);
  const audioProducts = useMemo(
    () => products.filter((product) => detectContentFormat(product) === 'audio'),
    [products],
  );
  const bookUploadedByOptions = useMemo(() => buildUploadedByOptions(bookProducts), [bookProducts]);
  const animationUploadedByOptions = useMemo(() => buildUploadedByOptions(animationProducts), [animationProducts]);
  const audioUploadedByOptions = useMemo(() => buildUploadedByOptions(audioProducts), [audioProducts]);
  const activeUploadedByOptions = useMemo(() => {
    if (activeSection === 'books') {
      return bookUploadedByOptions;
    }
    if (activeSection === 'animations') {
      return animationUploadedByOptions;
    }
    if (activeSection === 'audio') {
      return audioUploadedByOptions;
    }
    return [] as UploadedByOption[];
  }, [activeSection, audioUploadedByOptions, animationUploadedByOptions, bookUploadedByOptions]);
  const selectedUploadedByOption = useMemo(
    () => activeUploadedByOptions.find((option) => option.value === selectedUploadedBy) ?? null,
    [activeUploadedByOptions, selectedUploadedBy],
  );
  const filterProductsByScope = useCallback(
    (items: Product[]): Product[] => {
      if (contentFilterScope === 'user') {
        return selectedUploadedBy
          ? items.filter((product) => normalizeComparableValue(getProductUploaderDisplayName(product)) === selectedUploadedBy)
          : [];
      }

      return items;
    },
    [contentFilterScope, selectedUploadedBy],
  );
  const filteredBookProducts = useMemo(() => filterProductsByScope(bookProducts), [bookProducts, filterProductsByScope]);
  const filteredAnimationProducts = useMemo(() => filterProductsByScope(animationProducts), [animationProducts, filterProductsByScope]);
  const filteredAudioProducts = useMemo(() => filterProductsByScope(audioProducts), [audioProducts, filterProductsByScope]);
  const audioProductIds = useMemo(() => new Set(audioProducts.map((product) => product.id)), [audioProducts]);
  const animationProductIds = useMemo(() => new Set(animationProducts.map((product) => product.id)), [animationProducts]);
  const audioReviews = useMemo(
    () => productReviews.filter((review) => audioProductIds.has(review.productId)),
    [audioProductIds, productReviews],
  );
  const animationReviews = useMemo(
    () => productReviews.filter((review) => animationProductIds.has(review.productId)),
    [animationProductIds, productReviews],
  );
  const filteredAudioReviews = useMemo(
    () => audioReviews.filter((review) => filteredAudioProducts.some((product) => product.id === review.productId)),
    [audioReviews, filteredAudioProducts],
  );
  const filteredAnimationReviews = useMemo(
    () => animationReviews.filter((review) => filteredAnimationProducts.some((product) => product.id === review.productId)),
    [animationReviews, filteredAnimationProducts],
  );
  const filteredBooksPerCategory = useMemo(() => {
    const counts = new Map<string, number>();
    filteredBookProducts.forEach((product) => {
      const key = product.categoryId || product.categoryKey;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [filteredBookProducts]);
  const categoryNameById = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  );
  const orderById = useMemo(() => new Map(orders.map((order) => [order.id, order])), [orders]);
  const resolveProductName = useCallback(
    (nameKey: string): string => {
      const translated = t(nameKey);
      if (translated !== nameKey) {
        return translated;
      }
      const fallback = nameKey
        .split('.')
        .at(-1)
        ?.replace(/[-_]+/g, ' ')
        .trim();
      return fallback && fallback.length > 0 ? fallback : nameKey;
    },
    [t],
  );
  const paymentHistorySuccessfulCount =
    paymentHistoryStats?.successful ??
    paymentHistoryRows.filter((row) => ['paid', 'success', 'successful', 'completed'].includes(row.status.trim().toLowerCase()))
      .length;
  const paymentHistoryFailedCount =
    paymentHistoryStats?.failed ??
    paymentHistoryRows.filter((row) => ['failed', 'cancelled', 'canceled'].includes(row.status.trim().toLowerCase()))
      .length;
  const isCategorySubmitting = categoryFeedback.status === 'pending';
  const isUserCreating = userCreationFeedback.status === 'pending';
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [bookMediaFile, setBookMediaFile] = useState<File | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<typeof users[0] | null>(null);
  const [userEditRole, setUserEditRole] = useState<UserRole>('buyer');
  const [isUserEditing, setIsUserEditing] = useState(false);
  const [isUserDeleting, setIsUserDeleting] = useState(false);
  const [userEditFeedback, setUserEditFeedback] = useState<FormFeedback>(initialFeedback);
  const [, setUserImageFile] = useState<File | null>(null);
  const [userImagePreview, setUserImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (!showUserModal || !editingUser) {
      return;
    }

    setUserEditRole(editingUser.role);
    setUserEditFeedback(initialFeedback);
    setIsUserEditing(false);
    setIsUserDeleting(false);
  }, [showUserModal, editingUser]);

  useEffect(() => {
    if (selectedUploadedBy && !activeUploadedByOptions.some((option) => option.value === selectedUploadedBy)) {
      setSelectedUploadedBy('');
    }
  }, [activeUploadedByOptions, selectedUploadedBy]);

  const closeCreateUserModal = () => {
    setShowCreateUserModal(false);
    setNewUserPassword('');
    setUserCreationFeedback(initialFeedback);
  };

  const currentContentScopeLabel =
    contentFilterScope === 'all'
      ? 'All website content'
      : selectedUploadedByOption
        ? `${selectedUploadedByOption.label}'s uploads`
        : 'Select an uploader to view uploads';

  const renderContentFilterPanel = (
    contentLabel: string,
    totalCount: number,
    visibleCount: number,
    uploadedByOptions: UploadedByOption[],
  ) => (
    <article className="rounded-2xl border border-orange-100 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{contentLabel} visibility</h3>
          <p className="mt-1 text-sm text-slate-500">
            Choose whether to review all content on the website, uploads from one user, or your own uploads.
          </p>
        </div>
        <div className="rounded-xl bg-orange-50 px-3 py-2 text-right">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-700">Now Viewing</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{currentContentScopeLabel}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All Website Content' },
          { id: 'user', label: 'By User' },
        ].map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setContentFilterScope(option.id as ContentFilterScope)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
              contentFilterScope === option.id
                ? 'bg-orange-500 text-white shadow-[0_12px_28px_-18px_rgba(249,115,22,0.9)]'
                : 'border border-orange-200 bg-white text-orange-700 hover:bg-orange-50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,280px)_repeat(2,minmax(0,1fr))]">
        <label className="grid gap-1 text-sm font-medium text-slate-700">
          Uploaded By
          <select
            value={selectedUploadedBy}
            onChange={(event) => {
              setSelectedUploadedBy(event.target.value);
              setContentFilterScope('user');
            }}
            disabled={uploadedByOptions.length === 0}
            className="rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-orange-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100"
          >
            <option value="">Choose uploader...</option>
            {uploadedByOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} ({option.count})
              </option>
            ))}
          </select>
        </label>

        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Visible</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{visibleCount}</p>
          <p className="text-xs text-slate-500">Items in the current view.</p>
        </div>

        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{totalCount}</p>
          <p className="text-xs text-slate-500">Items of this content type on the website.</p>
        </div>
      </div>
    </article>
  );

  const handleCreateCategory = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = categoryName.trim();

    if (!trimmedName) {
      setCategoryFeedback({ status: 'error', message: 'Category name is required.' });
      return;
    }

    setCategoryFeedback({ status: 'pending', message: 'Creating category...' });
    setCategoryName('');
    setCategorySlug('');
    setCategoryAudience('mixed');
    setCategoryFeedback({
      status: 'success',
      message: `Category "${trimmedName}" created (local only).`,
    });
  };

  const handleCreateUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedName = newUserName.trim();
    const normalizedEmail = newUserEmail.trim().toLowerCase();

    if (!trimmedName) {
      setUserCreationFeedback({ status: 'error', message: 'Name is required.' });
      return;
    }

    if (!normalizedEmail) {
      setUserCreationFeedback({ status: 'error', message: 'Email is required.' });
      return;
    }

    if (newUserPassword.trim().length < 6) {
      setUserCreationFeedback({ status: 'error', message: 'Password must be at least 6 characters.' });
      return;
    }

    setUserCreationFeedback({ status: 'pending', message: 'Creating user...' });

    try {
      const emailLocalPart = normalizedEmail.split('@')[0] || 'reader';
      const username = buildSlug(emailLocalPart).replace(/-/g, '') || `reader${Date.now().toString().slice(-5)}`;
      const roleForApi = newUserRole === 'vendor' ? 'creator' : 'customer';
      const response = await authService.signup({
        username,
        fullname: trimmedName,
        telephone: '0000000000',
        email: normalizedEmail,
        password: newUserPassword,
        role: roleForApi,
        profileImageUrl: newUserPhotoUrl.trim() || undefined,
      });

      if (!response.success) {
        setUserCreationFeedback({
          status: 'error',
          message: response.message || 'Unable to create user.',
        });
        return;
      }

      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('buyer');
      setNewUserPhotoUrl('');
      setUserCreationFeedback({
        status: 'success',
        message: `User "${trimmedName}" created successfully.`,
      });
      await refreshUsers();
    } catch (error: unknown) {
      setUserCreationFeedback({
        status: 'error',
        message: toActionErrorMessage(error, 'Unable to create user.'),
      });
    }
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserImageFile(null);
    setUserImagePreview(null);
    setUserEditFeedback(initialFeedback);
    setIsUserEditing(false);
    setIsUserDeleting(false);
  };

  const handleUpdateUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingUser) return;

    if (userEditRole === editingUser.role) {
      setUserEditFeedback({ status: 'success', message: 'No changes to save.' });
      return;
    }

    setIsUserEditing(true);
    setUserEditFeedback({ status: 'pending', message: 'Updating user...' });
    try {
      await userService.assignUserRole(editingUser.id, userEditRole);
      await refreshUsers();
      setUserEditFeedback({ status: 'success', message: 'User updated successfully.' });
      setTimeout(() => {
        closeUserModal();
      }, 900);
    } catch (error: unknown) {
      setUserEditFeedback({ status: 'error', message: toActionErrorMessage(error, 'Unable to update user.') });
    } finally {
      setIsUserEditing(false);
    }
  };

  const handleApproveUser = async () => {
    if (!editingUser) return;

    setIsUserEditing(true);
    setUserEditFeedback({ status: 'pending', message: 'Updating verification...' });
    try {
      await userService.approveUser(editingUser.id);
      await refreshUsers();
      setUserEditFeedback({ status: 'success', message: 'Verification updated successfully.' });
    } catch (error: unknown) {
      setUserEditFeedback({ status: 'error', message: toActionErrorMessage(error, 'Unable to approve user.') });
    } finally {
      setIsUserEditing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!editingUser) return;

    const confirmed = typeof window === 'undefined' ? true : window.confirm(`Delete user "${editingUser.name}"?`);
    if (!confirmed) return;

    setIsUserDeleting(true);
    setUserEditFeedback({ status: 'pending', message: 'Deleting user...' });
    try {
      await userService.deleteUser(editingUser.id);
      await refreshUsers();
      setUserEditFeedback({ status: 'success', message: 'User deleted successfully.' });
      setTimeout(() => {
        closeUserModal();
      }, 800);
    } catch (error: unknown) {
      setUserEditFeedback({ status: 'error', message: toActionErrorMessage(error, 'Unable to delete user.') });
    } finally {
      setIsUserDeleting(false);
    }
  };

  const handleCreateProduct = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const contentName = productTitleKey.trim();
    const contentSummary = productDescriptionKey.trim();
    const trimmedCategoryKey = productCategoryKey.trim();
    const priceNumber = Number(productPrice);
    const trimmedMtn = mtnMoMoNumber.trim();
    const trimmedAirtel = airtelMoneyNumber.trim();
    const trimmedPaypal = paypalEmail.trim();
    const hasMedia =
      contentCreationMode === 'book'
        ? Boolean(contentBookFile)
        : contentCreationMode === 'audio'
          ? Boolean(contentAudioFile)
          : Boolean(contentVideoFile);

    if (!contentName) {
      setProductFeedback({
        status: 'error',
        message: contentCreationMode === 'pod' ? 'name is required.' : 'Content name is required.',
      });
      return;
    }
    if (contentCreationMode === 'pod') {
      const parsedPageCount = Number(podPageCount);
      if (!Number.isFinite(parsedPageCount) || parsedPageCount <= 0) {
        setProductFeedback({ status: 'error', message: 'pageCount is required and must be greater than 0.' });
        return;
      }

      setProductFeedback({ status: 'pending', message: 'Creating POD book...' });
      setUploadProgress(0);
      try {
        const createdPodBook = await podBooksService.createPodBook({
          name: contentName,
          pageCount: parsedPageCount,
          size: podSize,
          colorType: podColorType,
          coverType: podCoverType,
          interiorPdf: podInteriorPdfFile,
          coverPdf: podCoverPdfFile,
          interiorPdfUrl: podInteriorPdfUrl,
          coverPdfUrl: podCoverPdfUrl,
        });

        setContentCreationMode('book');
        setProductTitleKey('');
        setProductDescriptionKey('');
        setProductCategoryKey('');
        setProductPrice('');
        setPodPageCount('');
        setPodSize('');
        setPodColorType('');
        setPodCoverType('');
        setPodInteriorPdfFile(null);
        setPodCoverPdfFile(null);
        setPodInteriorPdfUrl('');
        setPodCoverPdfUrl('');
        setAcceptHaapaTerms(false);
        setProductFormResetKey((value) => value + 1);
        setProductFeedback({
          status: 'success',
          message: `"${createdPodBook.title}" POD book created successfully.`,
        });
        setUploadProgress(null);
        await refreshCatalog();
        return;
      } catch (error) {
        setProductFeedback({
          status: 'error',
          message: toActionErrorMessage(error, 'Unable to create POD book.'),
        });
        setUploadProgress(null);
        return;
      }
    }
    if (!contentSummary) {
      setProductFeedback({ status: 'error', message: 'Content summary is required.' });
      return;
    }
    const resolvedCategory =
      categories.find((category) => category.slug === trimmedCategoryKey || category.id === trimmedCategoryKey) ?? categories[0];
    if (!resolvedCategory) {
      setProductFeedback({ status: 'error', message: 'Select a category before creating content.' });
      return;
    }
    if (!Number.isFinite(priceNumber) || priceNumber < 0) {
      setProductFeedback({ status: 'error', message: 'Content price must be a valid number ≥ 0.' });
      return;
    }
    if (!hasMedia) {
      setProductFeedback({
        status: 'error',
        message:
          contentCreationMode === 'book'
            ? 'Attach a book file.'
            : contentCreationMode === 'audio'
              ? 'Attach an audio file.'
              : 'Attach a video file.',
      });
      return;
    }
    if (trimmedMtn && !/^(078|079)\d{7}$/.test(trimmedMtn)) {
      setProductFeedback({
        status: 'error',
        message: 'MTN MoMo number must start with 078 or 079 and contain 10 digits.',
      });
      return;
    }
    if (trimmedAirtel && !/^(073|072)\d{7}$/.test(trimmedAirtel)) {
      setProductFeedback({
        status: 'error',
        message: 'Airtel Money number must start with 073 or 072 and contain 10 digits.',
      });
      return;
    }
    if (!trimmedMtn && !trimmedAirtel && !trimmedPaypal) {
      setProductFeedback({
        status: 'error',
        message: 'Add at least one payout destination (MoMo, Airtel, or PayPal).',
      });
      return;
    }
    if (!acceptHaapaTerms) {
      setProductFeedback({
        status: 'error',
        message: 'Please accept the HAAPA terms and conditions to continue.',
      });
      return;
    }

    setProductFeedback({ status: 'pending', message: 'Creating content...' });
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('name', contentName);
      formData.append('price', Number(priceNumber.toFixed(2)).toString());
      formData.append('category', resolvedCategory.slug || resolvedCategory.id || 'uncategorized');
      formData.append('summary', contentSummary);
      formData.append('status', 'pending');
      if (currentUser?.id) formData.append('uploaderId', currentUser.id);
      if (currentUser?.name) {
        formData.append('uploaderName', currentUser.name);
        formData.append('uploadedBy', currentUser.name);
      }
      if (currentUser?.email) formData.append('uploaderEmail', currentUser.email);

      if (contentTags.trim()) formData.append('tags', contentTags.trim());
      if (contentAuthor.trim()) formData.append('author', contentAuthor.trim());
      if (contentCompany.trim()) formData.append('publishedCompany', contentCompany.trim());
      if (trimmedPaypal) formData.append('paypalEmail', trimmedPaypal);
      if (trimmedMtn) formData.append('MoMoNumber', trimmedMtn);
      if (trimmedAirtel) formData.append('AirtelMoneyNumber', trimmedAirtel);
      if (contentCoverFile) formData.append('cover', contentCoverFile);
      if (contentCreationMode === 'audio' && contentAudioFile) formData.append('audio', contentAudioFile);
      if (contentCreationMode === 'animation' && contentVideoFile) {
        formData.append('video', contentVideoFile);
        formData.append('madeIntoAnimation', 'true');
      }
      if (contentCreationMode === 'book' && contentBookFile) formData.append('book', contentBookFile);

      const token = authService.getToken();
      const headers: HeadersInit = { Accept: '*/*' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const endpoints = [`${API_BASE_URL}/books`, `${BACKEND_BASE_URL}/books`];
      let creationResponse: unknown = null;
      let lastError: unknown = null;

      for (let index = 0; index < endpoints.length; index += 1) {
        try {
          const response = await fetch(endpoints[index], { method: 'POST', headers, body: formData });
          if (!response.ok) {
            const message = await response.text();
            if ((response.status === 404 || response.status === 405) && index < endpoints.length - 1) {
              continue;
            }
            throw new Error(message || `Request failed (${response.status})`);
          }
          creationResponse = await response.json().catch(() => ({}));
          break;
        } catch (error: unknown) {
          lastError = error;
        }
      }

      if (!creationResponse) {
        throw lastError ?? new Error('Unable to save content.');
      }

      const asRecord = (value: unknown): Record<string, unknown> =>
        value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      const creationRecord = asRecord(creationResponse);
      const dataRecord = asRecord(creationRecord.data);
      const createdBookRecord = asRecord(creationRecord.book ?? dataRecord.book ?? creationRecord.data);
      const createdName =
        (typeof createdBookRecord.name === 'string' && createdBookRecord.name.trim()) ||
        (typeof createdBookRecord.title === 'string' && createdBookRecord.title.trim()) ||
        contentName;

      setProductTitleKey('');
      setProductDescriptionKey('');
      setContentCreationMode('book');
      setProductCategoryKey(resolvedCategory.slug || resolvedCategory.id || '');
      setProductPrice('');
      setContentTags('');
      setContentAuthor('');
      setContentCompany('');
      setContentAudioFile(null);
      setContentVideoFile(null);
      setContentBookFile(null);
      setContentCoverFile(null);
      setMtnMoMoNumber('');
      setAirtelMoneyNumber('');
      setPaypalEmail('');
      setAcceptHaapaTerms(false);
      setProductFormResetKey((value) => value + 1);
      setProductFeedback({ status: 'success', message: `"${createdName}" saved to the database.` });
      setUploadProgress(null);
      await refreshCatalog();
    } catch (error) {
      setProductFeedback({
        status: 'error',
        message: toActionErrorMessage(error, 'Unable to create content.'),
      });
      setUploadProgress(null);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    setProductFeedback({ status: 'pending', message: 'Deleting product...' });
    try {
      await deleteProduct(productId);
      setProductFeedback({ status: 'success', message: 'Product deleted successfully.' });
      await refreshCatalog();
    } catch (error) {
      setProductFeedback({
        status: 'error',
        message: toActionErrorMessage(error, 'Unable to delete product.'),
      });
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductTitleKey(product.nameKey);
    setProductDescriptionKey(product.descriptionKey);
    setProductCategoryKey(product.categoryKey);
    setProductPrice(product.price.toString());
    setProductCurrency('$');
    setProductLanguage('en');
    setProductAudience(product.audience || 'public');
    setProductCoverUrl(product.imageUrl || '');
    setProductContentUrl(product.contentUrl || '');
    setProductKind(detectContentFormat(product));
    setBookMediaFile(null);
    setUploadProgress(null);
    setShowEditProductModal(true);
  };

  const handleUpdateProduct = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingProduct) return;

    setProductFeedback({ status: 'pending', message: 'Updating product...' });
    try {
      const productId = editingProduct.digitalAssetId || editingProduct.id;

      await new Promise((resolve) => setTimeout(resolve, 250));
      setProductFeedback({ status: 'success', message: 'Product updated locally.' });
      setShowEditProductModal(false);
      setEditingProduct(null);
      setBookMediaFile(null);
      setUploadProgress(null);
      await refreshCatalog();
    } catch (error) {
      setProductFeedback({
        status: 'error',
        message: toActionErrorMessage(error, 'Unable to update product.'),
      });
    }
  };

  const handleUploadBookMedia = async () => {
    if (!editingProduct) {
      return;
    }

    if (!bookMediaFile) {
      setProductFeedback({ status: 'error', message: 'Choose an audio or video file first.' });
      return;
    }

    setProductFeedback({ status: 'pending', message: 'Uploading media...' });
    setUploadProgress(0);

    try {
      const payload = await booksService.uploadBookMedia(
        editingProduct.id,
        bookMediaFile,
        (progress) => setUploadProgress(Math.round(progress.percent)),
      );

      const record = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
      const data = record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : {};
      const book =
        (record.book && typeof record.book === 'object' ? (record.book as Record<string, unknown>) : null) ||
        (data.book && typeof data.book === 'object' ? (data.book as Record<string, unknown>) : null) ||
        data ||
        record;

      const nextAudioUrl = typeof book.audioUrl === 'string' ? book.audioUrl : undefined;
      const nextVideoUrl = typeof book.videoUrl === 'string' ? book.videoUrl : undefined;
      if (nextAudioUrl || nextVideoUrl) {
        setEditingProduct((previous) =>
          previous
            ? {
                ...previous,
                audioUrl: nextAudioUrl ?? previous.audioUrl,
                videoUrl: nextVideoUrl ?? previous.videoUrl,
              }
            : previous,
        );
      }

      setBookMediaFile(null);
      setProductFeedback({ status: 'success', message: 'Media uploaded successfully.' });
      setUploadProgress(null);
      await refreshCatalog();
    } catch (error) {
      setProductFeedback({ status: 'error', message: toActionErrorMessage(error, 'Unable to upload media.') });
      setUploadProgress(null);
    }
  };

  return (
    <>
    <ErrorBoundary>
      <DashboardFrame
      brand={t('dashboardAdmin.brand')}
      roleLabel={t('dashboardAdmin.roleLabel')}
      userName={currentUser?.name ?? t('dashboardAdmin.defaultUserName')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle={sectionMeta[activeSection].title}
      pageDescription={sectionMeta[activeSection].description}
      navItems={adminNavItems}
      activeNavId={activeSection}
      headerVariant="admin"
      showNotifications={false}
      showSettings={false}
      sidebarTogglePlacement="sidebar"
      headerAction={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowCreateUserModal(true)}
            className="rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600"
          >
            Create User
          </button>
          <button
            type="button"
            onClick={() => setShowProductModal(true)}
            className="rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
          >
            Create Content
          </button>
          <button
            type="button"
            className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {t('dashboardAdmin.actions.createUpdate')}
          </button>
        </div>
      }
    >
      {activeSection === 'overview' && (
        <OverviewDashboard
          greeting="WELCOME!"
          alertMessage="We regret to inform you that our server is currently experiencing temporary latency."
          variant="neo"
          statCards={[
            {
              id: 'total-categories',
              title: 'Categories',
              value: formatCompactNumber(categories.length),
              trendValue: '+2.3%',
              trendLabel: 'Last Week',
              isPositive: true,
              icon: LayoutDashboard,
            },
            {
              id: 'new-leads',
              title: 'Users',
              value: formatCompactNumber(users.length),
              trendValue: '+8.1%',
              trendLabel: 'Last Month',
              isPositive: true,
              icon: UsersRound,
            },
            {
              id: 'total-books',
              title: 'Books',
              value: formatCompactNumber(products.length),
              trendValue: '-0.3%',
              trendLabel: 'Last Month',
              isPositive: false,
              icon: BookCopy,
            },
            {
              id: 'booked-revenue',
              title: 'Booked Revenue',
              value: `$ ${formatDecimalCompact(stats.revenue)}`,
              trendValue: '-10.6%',
              trendLabel: 'Last Month',
              isPositive: false,
              icon: BarChart3,
            },
          ]}
          performanceBars={adminPerformance.bars}
          performanceLine={adminPerformance.line}
          conversionRate={conversionRate}
          thisWeekValue={formatDecimalCompact(stats.revenue * 0.18)}
          lastWeekValue={formatDecimalCompact(stats.revenue * 0.24)}
          topPages={adminTopPages}
          recentOrders={orders}
          users={users}
          tone={themeMode === 'dark' ? 'night' : 'light'}
          sideListTitle="Book of the Month Nominees"
          sideListItems={bookNominees}
          showUserDistribution={false}
          showRecentUsers
          showRecentOrders={false}
        />
      )}

      {activeSection === 'pod' && <PodManagerDashboardPanel />}

      {activeSection === 'books' && (
        <div className="space-y-4 pb-2">
          <div className="grid gap-4 sm:grid-cols-3">
            <InsightCard title="Books In View" value={`${filteredBookProducts.length}`} subtitle={currentContentScopeLabel} />
            <InsightCard title="Total Books" value={`${bookProducts.length}`} subtitle="All book titles on the website." />
            <InsightCard
              title="Archived In View"
              value={`${filteredBookProducts.filter((product) => product.stock === 0).length}`}
              subtitle="Out of stock / unpublished titles in this view."
            />
          </div>
          <article className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-orange-100 bg-white p-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Catalog Management</h3>
              <p className="mt-1 text-sm text-slate-500">
                Create categories and products, manage your book catalog.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowCategoryModal(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600"
              >
                + Create Category
              </button>
              <button
                type="button"
                onClick={() => setShowProductModal(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
              >
                + Create Content
              </button>
              <button
                type="button"
                onClick={() => {
                  void refreshCatalog();
                }}
                disabled={isCatalogLoading || isCategorySubmitting}
                className="inline-flex items-center gap-2 rounded-xl border border-orange-200 px-3 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <RefreshCcw className={`h-4 w-4 ${isCatalogLoading ? 'animate-spin' : ''}`} />
                {isCatalogLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </article>
          {renderContentFilterPanel('Books', bookProducts.length, filteredBookProducts.length, bookUploadedByOptions)}
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Categories</h3>
            {categories.length === 0 || filteredBookProducts.length === 0 ? (
              <p className="mt-3 rounded-lg bg-orange-50 p-3 text-sm text-slate-600">
                No book categories available for this selection.
              </p>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((category) => {
                  const count =
                    filteredBooksPerCategory.get(category.id) ??
                    (category.slug ? filteredBooksPerCategory.get(category.slug) : undefined) ??
                    0;
                  if (count === 0) {
                    return null;
                  }
                  return (
                    <span
                      key={category.id}
                      className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700"
                    >
                      {category.name}
                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-orange-600">
                        {count}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
          </article>
          <div className="overflow-x-auto rounded-2xl border border-orange-100 bg-white">
            <table className="min-w-full divide-y divide-orange-100">
              <thead className="bg-orange-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left">Book</th>
                  <th className="px-4 py-3 text-left">Uploaded By</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Price</th>
                  <th className="px-4 py-3 text-left">Stock</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100 text-sm text-slate-700">
                {filteredBookProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No books found for this selection.
                    </td>
                  </tr>
                ) : (
                  filteredBookProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="px-4 py-3 font-semibold">{t(product.nameKey)}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{getProductUploaderDisplayName(product)}</p>
                        <p className="text-xs text-slate-500">{getProductUploaderDisplayMeta(product)}</p>
                      </td>
                      <td className="px-4 py-3">
                        {product.categoryId
                          ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
                          : t(product.categoryKey)}
                      </td>
                      <td className="px-4 py-3">$ {product.price.toFixed(2)}</td>
                      <td className="px-4 py-3">{product.stock}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">Published</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditProduct(product)}
                            className="text-xs font-semibold text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              void handleDeleteProduct(product.id);
                            }}
                            className="text-xs font-semibold text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSection === 'animations' && (
        <div className="space-y-4 pb-2">
          {productFeedback.status === 'success' && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-sm text-green-700">
              {productFeedback.message}
            </div>
          )}
          {productFeedback.status === 'error' && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              {productFeedback.message}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <InsightCard
              title="Animations In View"
              value={String(filteredAnimationProducts.length)}
              subtitle={currentContentScopeLabel}
            />
            <InsightCard
              title="Animation Reviews In View"
              value={String(filteredAnimationReviews.length)}
              subtitle="Feedback items available for moderation."
            />
          </div>
          {renderContentFilterPanel('Animations', animationProducts.length, filteredAnimationProducts.length, animationUploadedByOptions)}
          <div className="overflow-x-auto rounded-2xl border border-orange-100 bg-white">
            <table className="min-w-full divide-y divide-orange-100">
              <thead className="bg-orange-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left">Animation</th>
                  <th className="px-4 py-3 text-left">Uploaded By</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Reviews</th>
                  <th className="px-4 py-3 text-left">Stock</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100 text-sm text-slate-700">
                {filteredAnimationProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No animation titles found for this selection.
                    </td>
                  </tr>
                ) : (
                  filteredAnimationProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="px-4 py-3 font-semibold">{t(product.nameKey)}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{getProductUploaderDisplayName(product)}</p>
                        <p className="text-xs text-slate-500">{getProductUploaderDisplayMeta(product)}</p>
                      </td>
                      <td className="px-4 py-3">
                        {product.categoryId
                          ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
                          : t(product.categoryKey)}
                      </td>
                      <td className="px-4 py-3">{product.reviewCount}</td>
                      <td className="px-4 py-3">{product.stock}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">Published</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditProduct(product)}
                            className="text-xs font-semibold text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              void handleDeleteProduct(product.id);
                            }}
                            className="text-xs font-semibold text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Recent Animation Reviews</h3>
            <div className="mt-3 space-y-2">
              {filteredAnimationReviews.length === 0 ? (
                <p className="rounded-lg bg-orange-50 p-3 text-sm text-slate-600">No animation reviews found for this selection.</p>
              ) : (
                filteredAnimationReviews.slice(0, 6).map((review, index) => (
                  <div key={`${review.productId}-${index}`} className="rounded-lg bg-orange-50 p-3 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">{review.productName}</p>
                    <p className="text-xs text-slate-500">By {review.author} · {review.rating}/5</p>
                    <p className="mt-1">{t(review.commentKey)}</p>
                  </div>
                ))
              )}
            </div>
          </article>
        </div>
      )}

      {activeSection === 'audio' && (
        <div className="space-y-4 pb-2">
          {productFeedback.status === 'success' && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-sm text-green-700">
              {productFeedback.message}
            </div>
          )}
          {productFeedback.status === 'error' && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              {productFeedback.message}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <InsightCard
              title="Audio In View"
              value={String(filteredAudioProducts.length)}
              subtitle={currentContentScopeLabel}
            />
            <InsightCard
              title="Audio Reviews In View"
              value={String(filteredAudioReviews.length)}
              subtitle="Feedback items available for moderation."
            />
          </div>
          {renderContentFilterPanel('Audio', audioProducts.length, filteredAudioProducts.length, audioUploadedByOptions)}
          <div className="overflow-x-auto rounded-2xl border border-orange-100 bg-white">
            <table className="min-w-full divide-y divide-orange-100">
              <thead className="bg-orange-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left">Audio</th>
                  <th className="px-4 py-3 text-left">Uploaded By</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Reviews</th>
                  <th className="px-4 py-3 text-left">Stock</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100 text-sm text-slate-700">
                {filteredAudioProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No audio titles found for this selection.
                    </td>
                  </tr>
                ) : (
                  filteredAudioProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="px-4 py-3 font-semibold">{t(product.nameKey)}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{getProductUploaderDisplayName(product)}</p>
                        <p className="text-xs text-slate-500">{getProductUploaderDisplayMeta(product)}</p>
                      </td>
                      <td className="px-4 py-3">
                        {product.categoryId
                          ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
                          : t(product.categoryKey)}
                      </td>
                      <td className="px-4 py-3">{product.reviewCount}</td>
                      <td className="px-4 py-3">{product.stock}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">Published</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditProduct(product)}
                            className="text-xs font-semibold text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              void handleDeleteProduct(product.id);
                            }}
                            className="text-xs font-semibold text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Recent Audio Reviews</h3>
            <div className="mt-3 space-y-2">
              {filteredAudioReviews.length === 0 ? (
                <p className="rounded-lg bg-orange-50 p-3 text-sm text-slate-600">No audio reviews found for this selection.</p>
              ) : (
                filteredAudioReviews.slice(0, 6).map((review, index) => (
                  <div key={`${review.productId}-${index}`} className="rounded-lg bg-orange-50 p-3 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">{review.productName}</p>
                    <p className="text-xs text-slate-500">By {review.author} · {review.rating}/5</p>
                    <p className="mt-1">{t(review.commentKey)}</p>
                  </div>
                ))
              )}
            </div>
          </article>
        </div>
      )}

      {activeSection === 'users' && (
        <div className="space-y-4 pb-2">
          <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-slate-900">Role Distribution</h3>
                <button
                  type="button"
                  onClick={() => {
                    void refreshUsers();
                  }}
                  className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                >
                  Refresh Users
                </button>
              </div>
              {isUsersLoading && (
                <p className="mt-2 text-xs text-slate-500">Loading users from database...</p>
              )}
              {!hasAuthToken && (
                <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-700">
                  You are signed in without an API token, so the dashboard may only show your local admin account. Please sign in using the real backend
                  admin account to load all users from the database.
                </p>
              )}
              <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
                <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                  <span>Customers / Students</span>
                  <strong>{roleDistribution.buyers}</strong>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                  <span>Vendors / Artists</span>
                  <strong>{roleDistribution.vendors}</strong>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                  <span>Admins</span>
                  <strong>{roleDistribution.admins}</strong>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-orange-100 px-3 py-2 text-slate-700">
                  <span>Total users</span>
                  <strong>{users.length}</strong>
                </div>
              </div>
              <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-3">
                <p className="rounded-lg bg-orange-50 p-2">Admin: Full access and moderation controls.</p>
                <p className="rounded-lg bg-orange-50 p-2">Vendor: Manage catalog, analytics, and payouts.</p>
                <p className="rounded-lg bg-orange-50 p-2">Buyer: Browse, purchase, and review content.</p>
              </div>
            </article>

            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-900">Add User</h3>
              <form onSubmit={handleCreateUser} className="mt-3 grid gap-3 text-sm">
                <label className="grid gap-1">
                  <span className="text-slate-600">Full Name</span>
                  <input
                    value={newUserName}
                    onChange={(event) => setNewUserName(event.target.value)}
                    placeholder="Jane Doe"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">Email</span>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(event) => setNewUserEmail(event.target.value)}
                    placeholder="jane@example.com"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">Password</span>
                  <input
                    type="password"
                    value={newUserPassword}
                    onChange={(event) => setNewUserPassword(event.target.value)}
                    placeholder="At least 6 characters"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1">
                    <span className="text-slate-600">Role</span>
                    <select
                      value={newUserRole}
                      onChange={(event) => setNewUserRole(event.target.value as 'buyer' | 'vendor')}
                      className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                    >
                      <option value="buyer">Buyer</option>
                      <option value="vendor">Vendor</option>
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-slate-600">Photo URL (optional)</span>
                    <input
                      type="url"
                      value={newUserPhotoUrl}
                      onChange={(event) => setNewUserPhotoUrl(event.target.value)}
                      placeholder="https://example.com/photo.jpg"
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={isUserCreating}
                  className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isUserCreating ? 'Creating...' : 'Create User'}
                </button>
                {userCreationFeedback.status !== 'idle' && (
                  <p className={`text-xs font-medium ${getFeedbackTone(userCreationFeedback.status)}`}>
                    {userCreationFeedback.message}
                  </p>
                )}
              </form>
            </article>
          </div>

          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-slate-900">All Users</h3>
              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                {users.length} total
              </span>
            </div>

            {users.length === 0 ? (
              <p className="mt-4 rounded-lg bg-orange-50 p-3 text-sm text-slate-600">
                No users found. Use the form above to add your first user.
              </p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {users.map((user) => (
                  <article key={user.id} className="rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                    <div className="flex items-center gap-3">
                      {user.profileImageUrl ? (
                        <img
                          src={user.profileImageUrl}
                          alt={user.name}
                          className="h-12 w-12 rounded-full object-cover ring-2 ring-orange-100"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-200 text-sm font-bold text-orange-800">
                          {buildInitials(user.name)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
                        <p className="truncate text-xs text-slate-600">{user.email}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      <span className={`rounded-full px-2 py-1 font-semibold ${getUserRoleTone(user.role)}`}>
                        {formatUserRoleLabel(user.role)}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-1 font-medium text-slate-600 capitalize">
                        {user.provider}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                        className="ml-auto text-xs font-semibold text-blue-600 hover:underline"
                      >
                        View More
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </article>
        </div>
      )}

      {activeSection === 'refunds' && <AdminRefundPage />}

      {activeSection === 'transfers' && <TransferProcessBoard role="admin" />}

      {activeSection === 'orders' && (
        <div className="overflow-x-auto rounded-2xl border border-orange-100 bg-white">
          <table className="min-w-full divide-y divide-orange-100">
            <thead className="bg-orange-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Items</th>
                <th className="px-4 py-3 text-left">Total</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Tracking</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-orange-100 text-sm text-slate-700">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No orders found yet.</td>
                </tr>
              ) : (
                orders.map((order, index) => {
                  const isDigitalOrder = isAutomaticDigitalOrder(order);
                  const selectedAdminStatus = toAdminOrderStatus(order.status);
                  const orderRowKey = `${order.id}-${order.createdAt}-${index}`;
                  const isUpdatingOrder = activeOrderUpdateId === order.id;
                  const isExpanded = expandedOrderId === order.id;
                  return (
                    <>
                      <tr key={orderRowKey}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{order.buyerName}</p>
                          <p className="text-xs text-slate-500">{order.buyerEmail}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          {order.items.slice(0, 2).map((item) => (
                            <p key={`${order.id}-${item.product.id}`} className="text-xs">
                              {item.quantity}x {t(item.product.nameKey)}
                            </p>
                          ))}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-900">$ {order.total.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          {isDigitalOrder ? (
                            <div>
                              <span className="rounded-full bg-blue-100 px-2.5 py-1.5 text-xs font-semibold text-blue-700">
                                {order.status}
                              </span>
                              <p className="mt-1 text-[11px] font-semibold text-slate-500">
                                Digital orders update automatically from payment.
                              </p>
                            </div>
                          ) : (
                            <>
                              <label htmlFor={`admin-status-${order.id}`} className="sr-only">Status</label>
                              <select
                                id={`admin-status-${order.id}`}
                                value={selectedAdminStatus}
                                disabled={isUpdatingOrder}
                                aria-busy={isUpdatingOrder}
                                onChange={(event) => {
                                  const nextStatus = event.target.value as OrderStatus;
                                  if (nextStatus === selectedAdminStatus) {
                                    return;
                                  }

                                  setActiveOrderUpdateId(order.id);
                                  setOrderUpdateFeedback({ status: 'pending', message: 'Updating order status...' });
                                  void (async () => {
                                    try {
                                      const payload: { status: string; trackingNumber?: string } = { status: toBackendWorkflowStatus(nextStatus) };
                                      await orderService.adminUpdateOrderStatus(order.id, payload);
                                      await refreshOrders();
                                      setOrderUpdateFeedback({ status: 'success', message: 'Order status changed successfully.' });
                                    } catch (error: unknown) {
                                      const err = error as { message?: string };
                                      setOrderUpdateFeedback({ status: 'error', message: err.message || 'Unable to update order.' });
                                    } finally {
                                      setActiveOrderUpdateId(null);
                                    }
                                  })();
                                }}
                                className="rounded-lg border border-orange-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:border-orange-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                              >
                                {adminOrderStatusOptions.map((status) => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                              {isUpdatingOrder && (
                                <p className="mt-1 text-[11px] font-semibold text-orange-600">Changing status...</p>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                            className="rounded-lg border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                          >
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${orderRowKey}-details`}>
                          <td colSpan={6} className="bg-white px-4 pb-5 pt-3">
                            <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reference</p>
                                  <p className="mt-1 text-sm font-semibold text-slate-900">{order.paymentReference || order.id}</p>
                                </div>
                                {order.trackingNumber && (
                                  <div className="text-left sm:text-right">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tracking Number</p>
                                    <p className="mt-1 text-sm font-semibold text-slate-900">{order.trackingNumber}</p>
                                  </div>
                                )}
                              </div>
                              <div className="mt-4">
                                <OrderStatusTracker status={order.status} history={order.statusHistory} variant="detailed" />
                              </div>
                              {orderUpdateFeedback.status !== 'idle' && (
                                <p className={`mt-3 text-xs font-semibold ${getFeedbackTone(orderUpdateFeedback.status)}`}>
                                  {orderUpdateFeedback.message}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                )})
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeSection === 'payment-history' && (
        <div className="space-y-4 pb-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Paid Books (All)</h3>
                <p className="mt-1 text-sm text-slate-500">Source: GET /payments/succeed (admin) / GET /payments/done (admin)</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  void loadLegacyPaidBooks();
                }}
                disabled={isLegacyPaidBooksLoading}
                className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isLegacyPaidBooksLoading ? 'Refreshing...' : 'Refresh Paid Books'}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                Rows: {legacyPaidBooks.length}
              </span>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-700">
                Paid: {legacyPaidBooks.filter((row) => isSuccessfulPaymentStatus(row.status)).length}
              </span>
              <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700">
                Pending: {legacyPaidBooks.filter((row) => row.status.trim().toLowerCase() === 'pending').length}
              </span>
            </div>
            {legacyPaidBooksError && (
              <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{legacyPaidBooksError}</p>
            )}
          </article>

          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Legacy Paid/Unpaid Books</h3>
                <p className="mt-1 text-sm text-slate-500">Sources: GET /payments/succeed (admin), GET /payments/done (admin), and GET /payments/unpaid (admin)</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    void loadLegacyPaidBooks();
                  }}
                  disabled={isLegacyPaidBooksLoading}
                  className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLegacyPaidBooksLoading ? 'Loading paid...' : `Load Paid (${legacyPaidBooks.length})`}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void loadLegacyUnpaidBooks();
                  }}
                  disabled={isLegacyUnpaidBooksLoading}
                  className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLegacyUnpaidBooksLoading ? 'Loading unpaid...' : `Load Unpaid (${legacyUnpaidBooks.length})`}
                </button>
              </div>
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Paid Books (All)</p>
                </div>
                {legacyPaidBooksError && (
                  <p className="px-3 py-3 text-xs font-semibold text-red-700">{legacyPaidBooksError}</p>
                )}
                {!legacyPaidBooksError && legacyPaidBooks.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-slate-500">No rows loaded yet.</p>
                ) : (
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-white text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Title</th>
                        <th className="px-3 py-2 text-left">Amount</th>
                        <th className="px-3 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {legacyPaidBooks.slice(0, 10).map((row) => (
                        <tr key={`paid-${row.id}-${row.createdAt}`}>
                          <td className="px-3 py-2">
                            <p className="font-semibold text-slate-900">{row.title}</p>
                            <p className="text-xs text-slate-500">{row.buyerEmail}</p>
                          </td>
                          <td className="px-3 py-2">{formatMoney(row.amount, row.currency)}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(row.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Unpaid Books (Admin)</p>
                </div>
                {legacyUnpaidBooksError && (
                  <p className="px-3 py-3 text-xs font-semibold text-red-700">{legacyUnpaidBooksError}</p>
                )}
                {!legacyUnpaidBooksError && legacyUnpaidBooks.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-slate-500">No rows loaded yet.</p>
                ) : (
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-white text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Title</th>
                        <th className="px-3 py-2 text-left">Buyer</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {legacyUnpaidBooks.slice(0, 10).map((row) => (
                        <tr key={`unpaid-${row.id}-${row.createdAt}`}>
                          <td className="px-3 py-2">
                            <p className="font-semibold text-slate-900">{row.title}</p>
                            <p className="text-xs text-slate-500">{formatMoney(row.amount, row.currency)}</p>
                          </td>
                          <td className="px-3 py-2">
                            <p className="text-sm font-medium text-slate-800">{row.buyerName}</p>
                            <p className="text-xs text-slate-500">{row.buyerEmail}</p>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-600">{row.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </article>

          <article className="overflow-x-auto rounded-2xl border border-orange-100 bg-white">
            <table className="min-w-full divide-y divide-orange-100">
              <thead className="bg-orange-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left">Title</th>
                  <th className="px-4 py-3 text-left">Buyer</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Date & Time</th>
                  <th className="px-4 py-3 text-left">Payment Type</th>
                  <th className="px-4 py-3 text-left">Transaction Ref</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100 text-sm text-slate-700">
                {isLegacyPaidBooksLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      Loading paid books...
                    </td>
                  </tr>
                ) : legacyPaidBooks.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No paid book records found.
                    </td>
                  </tr>
                ) : (
                  legacyPaidBooks.map((row, index) => {
                    const statusLabel = getPaymentStatusLabel(row.status);
                    const paymentRowKey = `${row.id}-${row.transactionRef || row.buyerEmail}-${row.createdAt}-${index}`;

                    return (
                      <tr key={paymentRowKey}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{row.title}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{row.buyerName}</p>
                          <p className="text-xs text-slate-500">{row.buyerEmail}</p>
                        </td>
                        <td className="px-4 py-3">{formatMoney(row.amount, row.currency)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDateTime(row.createdAt)}</td>
                        <td className="px-4 py-3 text-slate-700">{row.paymentType || '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{row.transactionRef || '-'}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusTone(statusLabel)}`}
                          >
                            {statusLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </article>

        </div>
      )}

      {activeSection === 'announcements' && (
        <div className="grid gap-4 pb-2 lg:grid-cols-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Send Notification</h3>
            <div className="mt-3 grid gap-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Audience</label>
              <div className="grid gap-2 sm:grid-cols-3">
                {[
                  { id: 'all', label: 'All users' },
                  { id: 'creators', label: 'Creators only' },
                  { id: 'preferred', label: 'Preferred user' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setNotificationAudience(option.id as typeof notificationAudience)}
                    className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                      notificationAudience === option.id
                        ? 'border-orange-200 bg-orange-50 text-orange-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {notificationAudience === 'preferred' && (
                <select
                  value={preferredNotificationEmail}
                  onChange={(event) => setPreferredNotificationEmail(event.target.value)}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                >
                  <option value="">Select user email</option>
                  {users
                    .filter((user) => Boolean(user.email))
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((user) => (
                      <option key={user.id} value={user.email}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                </select>
              )}

              <textarea
                rows={5}
                placeholder="Write the notification message…"
                value={notificationBodyDraft}
                onChange={(event) => setNotificationBodyDraft(event.target.value)}
                className="rounded-xl border border-orange-100 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
              />

              {notificationFeedback.status !== 'idle' && (
                <p
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold ${
                    notificationFeedback.status === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : notificationFeedback.status === 'error'
                        ? 'border-rose-200 bg-rose-50 text-rose-700'
                        : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}
                >
                  {notificationFeedback.message || (notificationFeedback.status === 'pending' ? 'Sending…' : '')}
                </p>
              )}
              <button
                type="button"
                onClick={() => void publishNotifications()}
                disabled={!notificationBodyDraft.trim() || (notificationAudience === 'preferred' && !preferredNotificationEmail.trim())}
                className={`rounded-xl px-4 py-2 text-sm font-semibold text-white transition ${
                  notificationBodyDraft.trim() && (notificationAudience !== 'preferred' || preferredNotificationEmail.trim())
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'cursor-not-allowed bg-slate-300'
                }`}
              >
                Send
              </button>
            </div>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-slate-900">Recent Notifications</h3>
              <button
                type="button"
                onClick={() => void loadAdminNotifications()}
                className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>
            {notificationsLoading && adminNotifications.length === 0 ? (
              <p className="mt-3 rounded-lg bg-orange-50 p-3 text-sm text-slate-600">Loading…</p>
            ) : adminNotifications.length === 0 ? (
              <p className="mt-3 rounded-lg bg-orange-50 p-3 text-sm text-slate-600">No notifications yet.</p>
            ) : (
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                {adminNotifications.slice(0, 8).map((notification) => (
                  <li key={notification.id} className="rounded-lg bg-orange-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{notification.sendTo || 'Recipient'}</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">Notification</p>
                    <p className="mt-1 text-xs text-slate-700 whitespace-pre-wrap">{notification.notificationBody}</p>
                    {notification.createdAt && (
                      <p className="mt-2 text-[11px] font-semibold text-slate-500">{new Date(notification.createdAt).toLocaleString()}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </article>
        </div>
      )}

      {activeSection === 'translation' && (
        <div className="grid gap-4 pb-2 md:grid-cols-2">
          {[
            { language: 'English', status: 'Complete', coverage: 100 },
            { language: 'French', status: 'In Review', coverage: 88 },
            { language: 'Swahili', status: 'In Review', coverage: 81 },
            { language: 'Kinyarwanda', status: 'Draft', coverage: 72 },
          ].map((item) => (
            <article key={item.language} className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">{item.language}</h3>
                <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">{item.status}</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">Coverage: {item.coverage}%</p>
              <div className="mt-2 h-2 rounded-full bg-orange-100">
                <div className="h-2 rounded-full bg-orange-500" style={{ width: `${item.coverage}%` }} />
              </div>
            </article>
          ))}
        </div>
      )}

      {activeSection === 'analytics' && (
        <div className="space-y-4 pb-2">
          <div className="grid gap-4 xl:grid-cols-2">
            <SparklineCard title="Digital Sales" subtitle="Weekly purchases" values={[14, 18, 16, 22, 26, 28, 25, 31]} />
            <SparklineCard title="Print Sales" subtitle="Weekly printed copies" values={[6, 8, 7, 10, 11, 13, 14, 15]} />
          </div>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Traffic Sources</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {[
                { label: 'Organic search', pct: '64%' },
                { label: 'Social media', pct: '23%' },
                { label: 'Referral', pct: '9%' },
                { label: 'Direct', pct: '4%' },
              ].map((item) => (
                <p key={item.label} className="rounded-lg bg-orange-50 px-3 py-2 text-sm text-slate-700">
                  <strong>{item.pct}</strong> {item.label}
                </p>
              ))}
            </div>
          </article>
        </div>
      )}

      {activeSection === 'reviews' && (
        <div className="space-y-3 pb-2">
          {productReviews.length === 0 ? (
            <article className="rounded-2xl border border-orange-100 bg-white p-4 text-sm text-slate-500">No reviews available.</article>
          ) : (
            productReviews.slice(0, 10).map((review, index) => (
              <article key={`${review.productId}-${index}`} className="rounded-2xl border border-orange-100 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-900">{review.productName}</h3>
                  <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">
                    {review.rating}/5
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">By {review.author}</p>
                <p className="mt-2 text-sm text-slate-700">{t(review.commentKey)}</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">Approve</button>
                  <button type="button" className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700">Reject</button>
                </div>
              </article>
            ))
          )}
        </div>
      )}

      {activeSection === 'contact' && <AdminContactMessagesPage />}

      {activeSection === 'security' && (
        <div className="space-y-3 pb-2">
          {[
            'Admin signed in from Kigali office network.',
            'Payout settings updated for vendor bank transfer.',
            'Failed login attempt blocked by fraud rules.',
            'Tax settings reviewed for Rwanda and Kenya.',
          ].map((log) => (
            <article key={log} className="rounded-2xl border border-orange-100 bg-white p-4 text-sm text-slate-700">
              {log}
            </article>
          ))}
        </div>
      )}

      {activeSection === 'settings' && (
        <div className="grid gap-4 pb-2 lg:grid-cols-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Commerce Setup</h3>
            <div className="mt-3 grid gap-3 text-sm">
              <label className="grid gap-1">
                <span className="text-slate-600">Currency</span>
                <select className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none">
                  <option>$</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">Tax / VAT</span>
                <input className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none" defaultValue="18%" />
              </label>
            </div>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Payments</h3>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              <label className="flex items-center gap-2"><input type="checkbox" defaultChecked /> Card gateway enabled</label>
              <label className="flex items-center gap-2"><input type="checkbox" defaultChecked /> PayPal enabled</label>
              <label className="flex items-center gap-2"><input type="checkbox" defaultChecked /> Mobile Money enabled</label>
            </div>
            <button type="button" className="mt-4 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white">
              Save Settings
            </button>
          </article>
        </div>
      )}
    </DashboardFrame>
    </ErrorBoundary>

    {/* Create User Modal */}
    {showCreateUserModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <article className="w-full max-w-md rounded-2xl border border-orange-100 bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Create User</h3>
            <button
              type="button"
              onClick={closeCreateUserModal}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleCreateUser} className="mt-4 grid gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-slate-600">Full Name</span>
              <input
                value={newUserName}
                onChange={(event) => setNewUserName(event.target.value)}
                placeholder="Jane Doe"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Email</span>
              <input
                type="email"
                value={newUserEmail}
                onChange={(event) => setNewUserEmail(event.target.value)}
                placeholder="jane@example.com"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Password</span>
              <input
                type="password"
                value={newUserPassword}
                onChange={(event) => setNewUserPassword(event.target.value)}
                placeholder="At least 6 characters"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">Role</span>
                <select
                  value={newUserRole}
                  onChange={(event) => setNewUserRole(event.target.value as 'buyer' | 'vendor')}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="buyer">Buyer</option>
                  <option value="vendor">Vendor</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">Photo URL (optional)</span>
                <input
                  type="url"
                  value={newUserPhotoUrl}
                  onChange={(event) => setNewUserPhotoUrl(event.target.value)}
                  placeholder="https://example.com/photo.jpg"
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isUserCreating}
                className="flex-1 rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isUserCreating ? 'Creating...' : 'Create User'}
              </button>
              <button
                type="button"
                onClick={closeCreateUserModal}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"
              >
                Cancel
              </button>
            </div>
            {userCreationFeedback.status !== 'idle' && (
              <p className={`text-xs font-medium ${getFeedbackTone(userCreationFeedback.status)}`}>
                {userCreationFeedback.message}
              </p>
            )}
          </form>
        </article>
      </div>
    )}

    {/* Category Modal */}
    {showCategoryModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <article className="w-full max-w-md rounded-2xl border border-orange-100 bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Create Category</h3>
            <button
              type="button"
              onClick={() => setShowCategoryModal(false)}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              x
            </button>
          </div>
          <form onSubmit={(e) => { void handleCreateCategory(e); setShowCategoryModal(false); }} className="mt-4 grid gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-slate-600">Category Name</span>
              <input
                value={categoryName}
                onChange={(event) => setCategoryName(event.target.value)}
                placeholder="Children Stories"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Slug (optional)</span>
              <input
                value={categorySlug}
                onChange={(event) => setCategorySlug(event.target.value)}
                placeholder="children-stories"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Audience</span>
              <select
                value={categoryAudience}
                onChange={(event) => setCategoryAudience(event.target.value as AdminAudience)}
                className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
              >
                <option value="mixed">Mixed / Public</option>
                <option value="kids">Kids</option>
                <option value="adult">Adult</option>
                <option value="adults">Adults</option>
                <option value="public">Public</option>
              </select>
            </label>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isCategorySubmitting || isCatalogLoading}
                className="flex-1 rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isCategorySubmitting ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowCategoryModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"
              >
                Cancel
              </button>
            </div>
            {categoryFeedback.status !== 'idle' && (
              <p className={`text-xs font-medium ${getFeedbackTone(categoryFeedback.status)}`}>
                {categoryFeedback.message}
              </p>
            )}
          </form>
        </article>
      </div>
    )}

    {/* Product Modal */}
    {showProductModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <article className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-orange-100 bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">
              {contentCreationMode === 'pod' ? 'Create POD Book' : 'Create Content'}
            </h3>
            <button
              type="button"
              onClick={() => setShowProductModal(false)}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              x
            </button>
          </div>
                    <form key={`${productFormResetKey}-${contentCreationMode}`} onSubmit={(e) => { void handleCreateProduct(e); }} className="mt-4 grid gap-4 text-sm">
            <section className="space-y-3 rounded-xl border border-orange-100 p-3">
              <div className="grid gap-1 sm:max-w-xs">
                <span className="text-slate-600">Content Type</span>
                <select
                  value={contentCreationMode}
                  onChange={(event) => handleContentCreationModeChange(event.target.value as 'book' | 'audio' | 'animation' | 'pod')}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="book">Book</option>
                  <option value="audio">Audio</option>
                  <option value="animation">Animation</option>
                  <option value="pod">POD Book</option>
                </select>
              </div>
              <p className="text-xs text-slate-500">
                {contentCreationMode === 'pod'
                  ? 'Create a POD book with only name and pageCount required. All other POD fields are optional.'
                  : contentCreationMode === 'book'
                    ? 'Upload a standard book with only the fields needed for book publishing.'
                    : contentCreationMode === 'audio'
                      ? 'Upload an audio title with only the fields needed for audio publishing.'
                      : 'Upload an animation title with only the fields needed for animation publishing.'}
              </p>
            </section>

            <section className="space-y-3 rounded-xl border border-orange-100 p-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900">
                  {contentCreationMode === 'pod' ? '1. POD Details' : '1. Content Info'}
                </h4>
                <span className="text-xs text-slate-500">
                  {contentCreationMode === 'pod' ? 'name and pageCount required' : 'Required'}
                </span>
              </div>
              <label className="grid gap-1">
                <span className="text-slate-600">{contentCreationMode === 'pod' ? 'name *' : 'Content Name'}</span>
                <input
                  value={productTitleKey}
                  onChange={(event) => setProductTitleKey(event.target.value)}
                  placeholder={contentCreationMode === 'pod' ? 'My POD title' : 'Content name...'}
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              {contentCreationMode !== 'pod' ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1">
                    <span className="text-slate-600">Content Price /USD($)</span>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-xs text-slate-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={productPrice}
                        onChange={(event) => setProductPrice(event.target.value)}
                        placeholder="Content price..."
                        className="w-full rounded-xl border border-orange-100 px-6 py-2 focus:border-orange-400 focus:outline-none"
                      />
                    </div>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-slate-600">Content Category</span>
                    <select
                      value={productCategoryKey}
                      onChange={(event) => setProductCategoryKey(event.target.value)}
                      className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                    >
                      <option value="">Content category...</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.slug || category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1">
                    <span className="text-slate-600">pageCount *</span>
                    <input
                      type="number"
                      min="1"
                      required
                      value={podPageCount}
                      onChange={(event) => setPodPageCount(event.target.value)}
                      placeholder="120"
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-slate-600">size</span>
                    <input
                      value={podSize}
                      onChange={(event) => setPodSize(event.target.value)}
                      placeholder="A5"
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                  </label>
                </div>
              )}
              {contentCreationMode !== 'pod' ? (
              <label className="grid gap-1">
                <span className="text-slate-600">Content Tags</span>
                <input
                  value={contentTags}
                  onChange={(event) => setContentTags(event.target.value)}
                  placeholder="Content tags..."
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              ) : null}
              {contentCreationMode !== 'pod' ? (
              <label className="grid gap-1">
                <span className="text-slate-600">Content Summary</span>
                <textarea
                  value={productDescriptionKey}
                  onChange={(event) => setProductDescriptionKey(event.target.value)}
                  placeholder="Content summary..."
                  rows={3}
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              ) : null}
            </section>

            {contentCreationMode !== 'pod' ? (
            <section className="space-y-3 rounded-xl border border-orange-100 p-3">
              <h4 className="text-sm font-semibold text-slate-900">2. Content Owner</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">Author Name</span>
                  <input
                    value={contentAuthor}
                    onChange={(event) => setContentAuthor(event.target.value)}
                    placeholder="Author name..."
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">Publishing or Production Company</span>
                  <input
                    value={contentCompany}
                    onChange={(event) => setContentCompany(event.target.value)}
                    placeholder="Publishing or Production company..."
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
            </section>
            ) : null}

            <section className="space-y-3 rounded-xl border border-orange-100 p-3">
              <h4 className="text-sm font-semibold text-slate-900">
                {contentCreationMode === 'pod' ? '2. POD Files And URLs' : '3. Content Medias'}
              </h4>
              {contentCreationMode === 'pod' ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-1">
                      <span className="text-slate-600">colorType</span>
                      <select
                        value={podColorType}
                        onChange={(event) => setPodColorType(event.target.value)}
                        className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                      >
                        <option value="">Select color type</option>
                        <option value="bw">Black & White</option>
                        <option value="color">Full Color</option>
                      </select>
                    </label>
                    <label className="grid gap-1">
                      <span className="text-slate-600">coverType</span>
                      <select
                        value={podCoverType}
                        onChange={(event) => setPodCoverType(event.target.value)}
                        className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                      >
                        <option value="">Select cover type</option>
                        <option value="soft">Soft Cover</option>
                        <option value="hard">Hard Cover</option>
                      </select>
                    </label>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-1">
                      <span className="text-slate-600">interiorPdf</span>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(event) => setPodInteriorPdfFile(event.target.files?.[0] ?? null)}
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                    </label>
                    <label className="grid gap-1">
                      <span className="text-slate-600">coverPdf</span>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(event) => setPodCoverPdfFile(event.target.files?.[0] ?? null)}
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                    </label>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-1">
                      <span className="text-slate-600">interiorPdfUrl</span>
                      <input
                        value={podInteriorPdfUrl}
                        onChange={(event) => setPodInteriorPdfUrl(event.target.value)}
                        placeholder="https://..."
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                    </label>
                    <label className="grid gap-1">
                      <span className="text-slate-600">coverPdfUrl</span>
                      <input
                        value={podCoverPdfUrl}
                        onChange={(event) => setPodCoverPdfUrl(event.target.value)}
                        placeholder="https://..."
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {contentCreationMode === 'audio' ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-2 sm:col-span-2">
                      <span className="text-slate-600">Audio File</span>
                      <input
                        type="file"
                        accept="audio/*,.mp3,.wav,.ogg"
                        onChange={(event) => setContentAudioFile(event.target.files?.[0] ?? null)}
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                      <button
                        type="button"
                        disabled
                        className="rounded-xl border border-dashed border-orange-200 px-3 py-2 text-xs font-semibold text-orange-600"
                      >
                        Start Audio Recording (coming soon)
                      </button>
                    </label>
                  </div>
                  ) : null}
                  {contentCreationMode === 'animation' ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-2 sm:col-span-2">
                      <span className="text-slate-600">Video File</span>
                      <input
                        type="file"
                        accept="video/*,.mp4,.webm"
                        onChange={(event) => setContentVideoFile(event.target.files?.[0] ?? null)}
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                      <button
                        type="button"
                        disabled
                        className="rounded-xl border border-dashed border-orange-200 px-3 py-2 text-xs font-semibold text-orange-600"
                      >
                        Start Video Recording (coming soon)
                      </button>
                    </label>
                  </div>
                  ) : null}
                  <div className="grid gap-3 sm:grid-cols-2">
                    {contentCreationMode === 'book' ? (
                    <label className="grid gap-1">
                      <span className="text-slate-600">Book File</span>
                      <input
                        type="file"
                        accept=".pdf,.epub,.mobi,.txt,application/pdf,application/epub+zip"
                        onChange={(event) => setContentBookFile(event.target.files?.[0] ?? null)}
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                    </label>
                    ) : null}
                    <label className="grid gap-1">
                      <span className="text-slate-600">
                        {contentCreationMode === 'animation' ? 'Animation Cover Image' : 'Content Cover Image'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(event) => setContentCoverFile(event.target.files?.[0] ?? null)}
                        className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                      />
                    </label>
                  </div>
                </>
              )}
            </section>

            {contentCreationMode !== 'pod' ? (
            <section className="space-y-3 rounded-xl border border-orange-100 p-3">
              <h4 className="text-sm font-semibold text-slate-900">4. Payment Information</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-slate-600">MTN Mobile Money Number</span>
                  <input
                    value={mtnMoMoNumber}
                    onChange={(event) => setMtnMoMoNumber(event.target.value)}
                    placeholder="MoMo number starts with 078 or 079"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-slate-600">Airtel Money Number</span>
                  <input
                    value={airtelMoneyNumber}
                    onChange={(event) => setAirtelMoneyNumber(event.target.value)}
                    placeholder="Airtel number starts with 073 or  072"
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                </label>
              </div>
              <label className="grid gap-1">
                <span className="text-slate-600">PayPal Email</span>
                <input
                  type="email"
                  value={paypalEmail}
                  onChange={(event) => setPaypalEmail(event.target.value)}
                  placeholder="PayPal email address..."
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
            </section>
            ) : null}

            {contentCreationMode !== 'pod' ? (
            <section className="space-y-3 rounded-xl border border-orange-100 p-3">
              <h4 className="text-sm font-semibold text-slate-900">4. Agreement With HAAPA</h4>
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={acceptHaapaTerms}
                  onChange={(event) => setAcceptHaapaTerms(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-400"
                />
                <span className="leading-relaxed">
                  Accept our terms and conditions of using our platform.{' '}
                  <Link to="/terms-of-use" className="text-orange-600 underline">
                    Click here to read our terms and conditions
                  </Link>
                </span>
              </label>
            </section>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={productFeedback.status === 'pending'}
                className="flex-1 rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {productFeedback.status === 'pending'
                  ? 'Saving...'
                  : contentCreationMode === 'pod'
                    ? 'Create POD Book'
                    : 'Create Content'}
              </button>
              <button
                type="button"
                onClick={() => setShowProductModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
            </div>

            {uploadProgress !== null && (
              <div className="space-y-1 rounded-xl bg-orange-50 p-3">
                <div className="flex items-center justify-between text-xs font-medium text-slate-600">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 rounded-full bg-orange-100">
                  <div
                    className="h-2 rounded-full bg-orange-500 transition-[width] duration-200"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {productFeedback.status !== 'idle' && (
              <p className="text-xs font-medium">
                {productFeedback.message}
              </p>
            )}
          </form>
        </article>
      </div>
    )}
    {/* Edit Product Modal */}
    {showEditProductModal && editingProduct && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <article className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-orange-100 bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Edit Book / Media Item</h3>
              <p className="text-xs text-slate-500 mt-1">ID: {editingProduct.id}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowEditProductModal(false);
                setEditingProduct(null);
                setBookMediaFile(null);
                setUploadProgress(null);
              }}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              x
            </button>
          </div>
          <form onSubmit={(e) => { void handleUpdateProduct(e); }} className="mt-4 grid gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-slate-600">Title Key</span>
              <input
                value={productTitleKey}
                onChange={(event) => setProductTitleKey(event.target.value)}
                placeholder="products.myStory.title"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Description Key</span>
              <input
                value={productDescriptionKey}
                onChange={(event) => setProductDescriptionKey(event.target.value)}
                placeholder="products.myStory.description"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">Category</span>
                <select
                  value={productCategoryKey}
                  onChange={(event) => setProductCategoryKey(event.target.value)}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.slug || category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">Kind</span>
                <select
                  value={productKind}
                  onChange={(event) => setProductKind(event.target.value as 'book' | 'audio' | 'animation')}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="book">Book</option>
                  <option value="audio">Audio</option>
                  <option value="animation">Animation</option>
                </select>
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">Language</span>
                <input
                  value={productLanguage}
                  onChange={(event) => setProductLanguage(event.target.value)}
                  placeholder="en"
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">Audience</span>
                <select
                  value={productAudience}
                  onChange={(event) => setProductAudience(event.target.value as typeof productAudience)}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="public">Public</option>
                  <option value="kids">Kids</option>
                  <option value="adult">Adult</option>
                  <option value="adults">Adults</option>
                  <option value="mixed">Mixed</option>
                </select>
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">Price</span>
                <input
                  value={productPrice}
                  onChange={(event) => setProductPrice(event.target.value)}
                  placeholder="12000"
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">Currency</span>
                <input
                  value={productCurrency}
                  onChange={(event) => setProductCurrency(event.target.value)}
                  placeholder="$"
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
            </div>
            <label className="grid gap-1">
              <span className="text-slate-600">Image URL</span>
              <input
                value={productCoverUrl}
                onChange={(event) => setProductCoverUrl(event.target.value)}
                placeholder="https://res.cloudinary.com/demo/image/upload/book.jpg"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Content URL</span>
              <input
                value={productContentUrl}
                onChange={(event) => setProductContentUrl(event.target.value)}
                placeholder="https://example.com/path/to/content.pdf"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>

            {productKind === 'book' && (
              <section className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
                <h4 className="text-sm font-semibold text-slate-900">Book Media (Audio / Video)</h4>
                <div className="mt-2 space-y-2 text-xs text-slate-700">
                  <p>
                    <span className="font-semibold">audioUrl:</span>{' '}
                    {editingProduct.audioUrl ? (
                      <a className="text-blue-600 hover:underline" href={editingProduct.audioUrl} target="_blank" rel="noreferrer">
                        {editingProduct.audioUrl}
                      </a>
                    ) : (
                      <span className="text-slate-500">Not set</span>
                    )}
                  </p>
                  <p>
                    <span className="font-semibold">videoUrl:</span>{' '}
                    {editingProduct.videoUrl ? (
                      <a className="text-blue-600 hover:underline" href={editingProduct.videoUrl} target="_blank" rel="noreferrer">
                        {editingProduct.videoUrl}
                      </a>
                    ) : (
                      <span className="text-slate-500">Not set</span>
                    )}
                  </p>

                  <label className="grid gap-1">
                    <span className="text-slate-600">Upload audio/* or video/*</span>
                    <input
                      type="file"
                      accept="audio/*,video/*"
                      onChange={(event) => setBookMediaFile(event.target.files?.[0] ?? null)}
                      className="rounded-xl border border-orange-200 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                  </label>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void handleUploadBookMedia()}
                      disabled={!bookMediaFile || productFeedback.status === 'pending'}
                      className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      Upload Media
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookMediaFile(null)}
                      disabled={!bookMediaFile || productFeedback.status === 'pending'}
                      className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </section>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={productFeedback.status === 'pending'}
                className="flex-1 rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
              >
                {productFeedback.status === 'pending' ? 'Updating...' : 'Update Book / Media'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEditProductModal(false);
                  setEditingProduct(null);
                  setBookMediaFile(null);
                  setUploadProgress(null);
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"
              >
                Cancel
              </button>
            </div>
            {uploadProgress !== null && (
              <div className="space-y-1 rounded-xl bg-orange-50 p-3">
                <div className="flex items-center justify-between text-xs font-medium text-slate-600">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 rounded-full bg-orange-100">
                  <div
                    className="h-2 rounded-full bg-orange-500 transition-[width] duration-200 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            {productFeedback.status !== 'idle' && (
              <p className={`text-xs font-medium ${getFeedbackTone(productFeedback.status)}`}>
                {productFeedback.message}
              </p>
            )}
          </form>
        </article>
      </div>
    )}

    {/* User Details Modal */}
    {showUserModal && editingUser && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <article className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-orange-100 bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-slate-900">Edit User</h3>
              {typeof editingUser.verified === 'boolean' && (
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    editingUser.verified ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {editingUser.verified ? 'Verified' : 'Unverified'}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={closeUserModal}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              x
            </button>
          </div>
          <form onSubmit={(e) => { void handleUpdateUser(e); }} className="mt-4 space-y-3 text-sm">
            <div className="flex items-center gap-4 rounded-xl bg-orange-50 p-4">
              {userImagePreview || editingUser.profileImageUrl ? (
                <img
                  src={userImagePreview || editingUser.profileImageUrl}
                  alt={editingUser.name}
                  className="h-20 w-20 rounded-full object-cover ring-2 ring-orange-200"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-orange-200 text-lg font-bold text-orange-800">
                  {buildInitials(editingUser.name)}
                </div>
              )}
              <div className="flex-1">
                <p className="text-base font-semibold text-slate-900">{editingUser.name}</p>
                <p className="text-xs text-slate-500">ID: {editingUser.id}</p>
                <label className="mt-2 inline-block cursor-pointer rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-orange-600">
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setUserImageFile(file);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setUserImagePreview(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
              </div>
            </div>
            <label className="grid gap-1">
              <span className="text-slate-600">Full Name</span>
              <input
                defaultValue={editingUser.name}
                disabled
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Email</span>
              <input
                type="email"
                defaultValue={editingUser.email}
                disabled
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">Role</span>
                <select
                  value={userEditRole}
                  onChange={(event) => setUserEditRole(event.target.value as UserRole)}
                  disabled={isUserEditing || isUserDeleting}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                >
                  <option value="buyer">Buyer</option>
                  <option value="vendor">Vendor</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">Provider</span>
                <input
                  defaultValue={editingUser.provider}
                  disabled
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
            </div>
            <label className="grid gap-1">
              <span className="text-slate-600">Phone</span>
              <input
                defaultValue={editingUser.phone || ''}
                placeholder="Optional"
                disabled
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Country</span>
              <input
                defaultValue={editingUser.country || ''}
                placeholder="Optional"
                disabled
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Date of Birth</span>
              <input
                type="date"
                defaultValue={editingUser.dateOfBirth || ''}
                disabled
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Preferred Language</span>
              <select
                defaultValue={editingUser.preferredLanguage || 'en'}
                disabled
                className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
              >
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="sw">Swahili</option>
                <option value="rw">Kinyarwanda</option>
              </select>
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">Profile Image URL</span>
              <input
                type="url"
                defaultValue={editingUser.profileImageUrl || ''}
                placeholder="https://example.com/photo.jpg"
                disabled
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 text-xs text-slate-600">
              Tip: Role and verification updates sync with the database. Profile fields are read-only in this dashboard.
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="submit"
                disabled={isUserEditing || isUserDeleting}
                className="flex-1 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isUserEditing ? 'Updating...' : 'Update User'}
              </button>
              <button
                type="button"
                onClick={() => { void handleApproveUser(); }}
                disabled={isUserEditing || isUserDeleting}
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {typeof editingUser.verified === 'boolean' ? (editingUser.verified ? 'Unverify' : 'Verify') : 'Approve'}
              </button>
              <button
                type="button"
                onClick={() => { void handleDeleteUser(); }}
                disabled={isUserEditing || isUserDeleting}
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isUserDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                onClick={closeUserModal}
                disabled={isUserEditing || isUserDeleting}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Cancel
              </button>
            </div>
            {userEditFeedback.status !== 'idle' && (
              <p className={`text-xs font-medium ${getFeedbackTone(userEditFeedback.status)}`}>
                {userEditFeedback.message}
              </p>
            )}
          </form>
        </article>
      </div>
    )}

    {orderUpdateFeedback.status !== 'idle' && (
      <div className="fixed bottom-6 right-6 z-50 max-w-sm">
        <div
          className={`rounded-2xl border px-4 py-3 shadow-lg ${
            orderUpdateFeedback.status === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : orderUpdateFeedback.status === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-orange-200 bg-orange-50 text-orange-700'
          }`}
          role="status"
          aria-live="polite"
        >
          <p className="text-sm font-semibold">{orderUpdateFeedback.message}</p>
        </div>
      </div>
    )}
  </>
  );
};

export default AdminDashboardPage;



